// ApiService.js - Serviço para comunicação com nossa API personalizada
import API_URLS from './apiConfig';
import cacheService from './CacheService';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('auth_token');
    this.user = JSON.parse(localStorage.getItem('user_data') || 'null');
    this.useDirectUrls = localStorage.getItem('use_direct_urls') === 'true';
  }

  // Método para obter a URL de login adequada
  getLoginUrl() {
    return this.useDirectUrls ? API_URLS.login_direct : API_URLS.login;
  }
  
  // Método para obter a URL da API adequada
  getApiUrl() {
    return this.useDirectUrls ? API_URLS.api_direct : API_URLS.api;
  }
  
  // Método para obter todas as URLs da API
  getApiUrls() {
    return API_URLS;
  }
  
  // Método para obter o token de autenticação
  getToken() {
    return this.token;
  }

  // Método para fazer login
  async login(username, senha) {
    try {
      // Usar apenas a URL direta do Cloud Run que sabemos que funciona
      const loginUrl = API_URLS.login_direct;
      
      try {
        const response = await fetch(loginUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Origin': window.location.origin,
            'Accept': 'application/json'
          },
          body: JSON.stringify({ username, senha }),
          mode: 'cors',
          credentials: 'omit'
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Armazenar o token e as informações do usuário
          this.token = data.token;
          this.user = data.user;
          
          localStorage.setItem('auth_token', data.token);
          localStorage.setItem('user_data', JSON.stringify(data.user));
          localStorage.setItem('use_direct_urls', 'true');
          this.useDirectUrls = true;
          
          return { success: true, user: data.user };
        }
        
        // Se a resposta não foi ok, tentar obter a mensagem de erro
        const errorData = await response.json();
        return { 
          success: false, 
          error: errorData.error || 'Usuário ou senha incorretos',
          code: response.status
        };
        
      } catch (error) {
        return { 
          success: false, 
          error: 'Erro de conexão com o servidor.',
          details: error.message
        };
      }
    } catch (error) {
      return { 
        success: false, 
        error: 'Erro ao processar login.',
        details: error.message
      };
    }
  }

  // Método para verificar se o usuário está autenticado
  isAuthenticated() {
    if (!this.token || !this.user) {
      return false;
    }

    // Verificar se o token ainda é válido
    try {
      const tokenData = JSON.parse(atob(this.token.split('.')[1]));
      const expirationTime = tokenData.exp * 1000; // Converter para milissegundos
      
      if (Date.now() >= expirationTime) {
        this.logout();
        return false;
      }

      // Verificar se o usuário tem role válido
      if (this.user.role === 'inativo') {
        // Não fazer logout aqui, apenas retornar false
        return false;
      }

      return true;
    } catch (error) {
      this.logout();
      return false;
    }
  }

  // Obter dados do usuário atual
  getCurrentUser() {
    if (!this.token || !this.user) {
      return null;
    }
    return this.user;
  }

  // Método para obter dados do usuário
  async getUserInfo() {
    return this.callApi('');
  }

  // Método para listar orçamentos com múltiplas tentativas de URLs
  async getOrcamentos() {
    // Verificar se há orçamentos em cache antes de chamar a API
    const cachedOrcamentos = cacheService.getOrcamentos();
    if (cachedOrcamentos) {
      
      return { success: true, data: { orcamentos: cachedOrcamentos } };
    }
    
    // URLs para tentar, em ordem de prioridade
    let urls = [
      API_URLS.orcamentos,           // URL principal via Firebase Hosting
      API_URLS.orcamentos_alt,       // URL alternativa via Cloud Functions
      API_URLS.orcamentos_direct,    // URL direta para a API
      `${API_URLS.api}orcamentos`,   // URL relativa à base da API
      `${API_URLS.api_direct}orcamentos` // URL relativa à base da API direta
    ];
    
    // Se estiver em ambiente de desenvolvimento (localhost), priorize as URLs diretas
    if (window.location.hostname === 'localhost') {
      urls = [
        API_URLS.orcamentos_direct,    // URL direta para a API
        `${API_URLS.api_direct}orcamentos`, // URL relativa à base da API direta
        API_URLS.orcamentos_alt,       // URL alternativa via Cloud Functions
        API_URLS.orcamentos,           // URL principal via Firebase Hosting
        `${API_URLS.api}orcamentos`    // URL relativa à base da API
      ];
      
    }
    
    
    
    // Tentar cada URL até que uma funcione
    let lastError = null;
    let lastResponse = null;
    
    for (let i = 0; i < urls.length; i++) {
      try {
        const currentUrl = urls[i];
        
        
        // Tentar fetch com a URL atual
        const response = await fetch(currentUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json',
            'Origin': window.location.origin
          },
          mode: 'cors',
          credentials: 'omit'
        });
        
        
        lastResponse = response;
        
        // Se for bem-sucedido, processar os dados
        if (response.ok) {
          const data = await response.json();
          
          
          // Armazenar em cache
          if (data && data.orcamentos) {
            cacheService.setOrcamentos(data.orcamentos);
          }
          
          // Registrar qual URL funcionou para priorização futura
          localStorage.setItem('last_working_orcamentos_url', currentUrl);
          
          return { success: true, data };
        }
        
        // Se não foi OK, tentar obter mensagem de erro
        try {
          const errorData = await response.json();
          lastError = { 
            message: errorData.error || `Erro no servidor: ${response.status}`,
            code: response.status
          };
        } catch (e) {
          lastError = { 
            message: `Erro no servidor: ${response.status}`,
            code: response.status
          };
        }
        
      } catch (error) {
        
        lastError = error;
        // Continuar para a próxima URL
      }
    }
    
    // Se chegamos aqui, nenhuma URL funcionou
    
    
    // Verificar se existe algum orçamento salvo localmente como último recurso
    try {
      const localOrcamentos = localStorage.getItem('orcamentos');
      if (localOrcamentos) {
        const parsedOrcamentos = JSON.parse(localOrcamentos);
        
        return { 
          success: true, 
          data: { orcamentos: parsedOrcamentos },
          offlineMode: true
        };
      }
    } catch (e) {
      
    }
    
    // Retornar erro
    return { 
      success: false, 
      error: 'Não foi possível conectar ao servidor para buscar orçamentos',
      details: lastError ? lastError.message : 'Erro desconhecido',
      originalError: lastError
    };
  }

  // Método para normalizar o ID do orçamento
  normalizeOrcamentoId(empresa, cliente) {
    if (!empresa) return '';
    
    // Normalizar a empresa e cliente (remover acentos, espaços, etc)
    const normalizeText = (text) => {
      if (!text) return '';
      
      // Converter para minúsculas e remover espaços no início e fim
      let normalized = text.trim().toLowerCase();
      
      // Remover acentos
      normalized = normalized.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      
      // Substituir espaços e caracteres especiais por underscore
      normalized = normalized.replace(/[^a-z0-9]/g, '_');
      
      // Remover underscores múltiplos
      normalized = normalized.replace(/_+/g, '_');
      
      // Remover underscore no início e no fim
      normalized = normalized.replace(/^_|_$/g, '');
      
      return normalized;
    };
    
    // Normalizar empresa e cliente
    const empresaNormalizada = normalizeText(empresa);
    const clienteNormalizado = cliente ? normalizeText(cliente) : '';
    
    // Gerar um hash aleatório curto (8 caracteres)
    const generateShortHash = () => {
      const timestamp = Date.now().toString();
      const randomString = Math.random().toString(36).substring(2, 8);
      return timestamp.substring(timestamp.length - 4) + randomString.substring(0, 4);
    };
    
    const hash = generateShortHash();
    
    // Criar ID baseado na empresa, cliente e hash
    if (clienteNormalizado) {
      return `${empresaNormalizada}_${clienteNormalizado}_${hash}`;
    } else {
      return `${empresaNormalizada}_${hash}`;
    }
  }

  // Método para obter um orçamento específico por ID
  async getOrcamento(id) {
    if (!id) {
      
      return { success: false, error: 'ID do orçamento não fornecido' };
    }
    
    // Verificar primeiro se existe no cache
    const cachedOrcamentos = cacheService.getOrcamentos();
    if (cachedOrcamentos) {
      const orcamento = cachedOrcamentos.find(o => o.id === id);
      if (orcamento) {
        
        return { success: true, data: orcamento };
      }
    }
    
    // URLs para tentar, em ordem de prioridade
    let urls = [
      API_URLS.getOrcamentoUrl(id),        // URL principal via Firebase Hosting
      API_URLS.getOrcamentoAltUrl(id),     // URL alternativa via Cloud Functions
      API_URLS.getOrcamentoDirectUrl(id),  // URL direta para a API
      `${API_URLS.api}orcamento/${id}`,    // URL relativa à base da API
      `${API_URLS.api_direct}orcamento/${id}` // URL relativa à base da API direta
    ];
    
    // Se estiver em ambiente de desenvolvimento (localhost), priorize as URLs diretas
    if (window.location.hostname === 'localhost') {
      urls = [
        API_URLS.getOrcamentoDirectUrl(id),  // URL direta para a API
        `${API_URLS.api_direct}orcamento/${id}`, // URL relativa à base da API direta
        API_URLS.getOrcamentoAltUrl(id),     // URL alternativa via Cloud Functions
        API_URLS.getOrcamentoUrl(id),        // URL principal via Firebase Hosting
        `${API_URLS.api}orcamento/${id}`     // URL relativa à base da API
      ];
      
    }
    
    
    
    // Tentar cada URL até que uma funcione
    let lastError = null;
    
    for (let i = 0; i < urls.length; i++) {
      try {
        const currentUrl = urls[i];
        
        
        // Tentar fetch com a URL atual
        const response = await fetch(currentUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json',
            'Origin': window.location.origin
          },
          mode: 'cors',
          credentials: 'omit'
        });
        
        
        
        // Se for bem-sucedido, processar os dados
        if (response.ok) {
          const data = await response.json();
          
          
          // Armazenar em cache
          cacheService.addOrcamento(data);
          
          // Registrar qual URL funcionou para priorização futura
          localStorage.setItem('last_working_orcamento_url', currentUrl);
          
          return { success: true, data };
        }
        
        // Se não foi OK, tentar obter mensagem de erro
        try {
          const errorData = await response.json();
          lastError = { 
            message: errorData.error || `Erro no servidor: ${response.status}`,
            code: response.status
          };
        } catch (e) {
          lastError = { 
            message: `Erro no servidor: ${response.status}`,
            code: response.status
          };
        }
        
      } catch (error) {
        
        lastError = error;
        // Continuar para a próxima URL
      }
    }
    
    // Se chegamos aqui, nenhuma URL funcionou
    
    
    // Verificar se existe o orçamento salvo localmente como último recurso
    try {
      const localOrcamentos = localStorage.getItem('orcamentos');
      if (localOrcamentos) {
        const parsedOrcamentos = JSON.parse(localOrcamentos);
        const localOrcamento = parsedOrcamentos.find(o => o.id === id);
        
        if (localOrcamento) {
          
          return { 
            success: true, 
            data: localOrcamento,
            offlineMode: true
          };
        }
      }
    } catch (e) {
      
    }
    
    // Retornar erro
    return { 
      success: false, 
      error: 'Não foi possível conectar ao servidor para buscar o orçamento',
      details: lastError ? lastError.message : 'Erro desconhecido',
      originalError: lastError
    };
  }

  // Método para obter um projeto completo com todos seus detalhes
  async getProjeto(id) {
    
    
    try {
      // Usar a URL direta da Cloud Function para garantir compatibilidade
      const url = `${API_URLS.projeto}/${id}`;
      
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        
        return { success: false, error: errorData.error || `Erro no servidor: ${response.status}` };
      }
      
      const data = await response.json().catch(e => {
        
        return { error: 'Formato de resposta inválido' };
      });
      
      
      return { success: true, data };
    } catch (error) {
      
      
      // Tentar novamente usando o método callApi como fallback
      
      return this.callApi(`projeto/${id}`);
    }
  }

  // Método para obter itens de um projeto
  async getProjetoItens(projetoId) {
    
    
    // Verificar se há itens em cache antes de chamar a API
    const cachedItens = cacheService.getItens(projetoId);
    if (cachedItens) {
      
      return { success: true, data: { itens: cachedItens } };
    }
    
    // Se não houver no cache, chamar a API
    try {
      const url = API_URLS.getProjetoItemsUrl(projetoId);
      
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      if (!response.ok) {
        // Tentar o método callApi como fallback
        
        const result = await this.callApi(`projeto-itens/${projetoId}`);
        
        // Se bem-sucedido, armazenar no cache
        if (result.success && result.data && result.data.itens) {
          cacheService.setItens(projetoId, result.data.itens);
        }
        
        return result;
      }
      
      const data = await response.json();
      
      
      // Armazenar no cache
      if (data && data.itens) {
        cacheService.setItens(projetoId, data.itens);
      }
      
      return { success: true, data };
    } catch (error) {
      
      return { success: false, error: error.message || 'Erro desconhecido ao obter itens' };
    }
  }

  // Método para criar um novo orçamento
  async createOrcamento(orcamentoData) {
    try {
      
      
      // Obter todas as URLs possíveis para criar orçamento
      const urls = API_URLS.getOrcamentosUrls();
      let lastError = null;
      
      // Tentar cada URL até uma funcionar
      for (const url of urls) {
        try {
          
          
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${this.token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(orcamentoData)
          });
          
          const data = await response.json().catch(e => ({ error: 'Formato de resposta inválido' }));
          
          if (response.ok) {
            
            
            // Se a criação for bem-sucedida, adicionar ao cache
            if (data && data.orcamento) {
              cacheService.addOrcamento(data.orcamento);
            }
            
            return { success: true, data };
          }
          
          lastError = { status: response.status, error: data.error };
          
        } catch (error) {
          lastError = error;
          
        }
      }
      
      // Se chegou aqui, todas as tentativas falharam
      
      return { 
        success: false, 
        error: lastError?.error || 'Não foi possível criar o orçamento. Tente novamente mais tarde.',
        details: lastError
      };
    } catch (error) {
      
      return { 
        success: false, 
        error: 'Erro inesperado ao criar orçamento. Tente novamente.',
        details: error.message 
      };
    }
  }

  /**
   * Atualiza um orçamento existente e limpa o cache
   * @param {string} id - ID do orçamento a ser atualizado
   * @param {Object} orcamentoData - Dados do orçamento a serem atualizados
   * @returns {Promise<Object>} - Resultado da operação
   */
  async updateOrcamento(id, orcamentoData) {
    try {
      if (!id) {
        
        return { success: false, error: 'ID do orçamento é obrigatório' };
      }

      

      // Limpar o cache do orçamento antes da atualização
      if (typeof cacheService !== 'undefined') {
        cacheService.removeOrcamento(id);
      }

      // Lista de URLs para tentar em ordem
      const urls = [
        `${API_URLS.api_direct}orcamento/${id}`,
        `${API_URLS.api_direct}orcamentos/${id}`,
        `${API_URLS.orcamento_direct}/${id}`,
        `${API_URLS.orcamentos_direct}/${id}`,
        `${API_URLS.orcamento_alt}/${id}`,
        `${API_URLS.orcamentos_alt}/${id}`
      ];

      // Usar o método _requestWithFallback para maior robustez
      const result = await this._requestWithFallback(
        urls,
        'PUT',
        orcamentoData,
        true,
        'Erro ao atualizar orçamento'
      );

      if (result.success) {
        
        
        // Atualizar cache se disponível
        if (typeof cacheService !== 'undefined' && result.data) {
          cacheService.addOrcamento(result.data);
        }
        
        // Atualizar cache local se existir
        try {
          const cachedOrcamentos = localStorage.getItem('cached_orcamentos');
          if (cachedOrcamentos) {
            const orcamentos = JSON.parse(cachedOrcamentos);
            const index = orcamentos.findIndex(o => o.id === id);
            if (index !== -1) {
              orcamentos[index] = { ...orcamentos[index], ...orcamentoData };
              localStorage.setItem('cached_orcamentos', JSON.stringify(orcamentos));
              
            }
          }
        } catch (cacheError) {
          
        }
      } else {
        
      }

      return result;
    } catch (error) {
      
      return {
        success: false,
        error: 'Erro ao atualizar orçamento',
        details: error.message
      };
    }
  }
  /**
   * Exclui um orçamento pelo ID
   * @param {string} id - ID do orçamento a ser excluído
   * @returns {Promise<Object>} - Resultado da operação
   */
  async deleteOrcamento(id) {
    try {
      if (!id) {
        
        return { success: false, error: 'ID do orçamento é obrigatório' };
      }

      

      // Lista de URLs para tentar em ordem
      const urls = [
        `${API_URLS.api_direct}orcamento/${id}`,
        `${API_URLS.api_direct}orcamentos/${id}`,
        `${API_URLS.orcamento_direct}/${id}`,
        `${API_URLS.orcamentos_direct}/${id}`,
        `${API_URLS.orcamento_alt}/${id}`,
        `${API_URLS.orcamentos_alt}/${id}`
      ];

      let lastError = null;

      // Tentar cada URL até uma funcionar
      for (const url of urls) {
        try {
          

          const response = await fetch(url, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${this.token}`,
              'Content-Type': 'application/json'
            }
          });

          const data = await response.json().catch(() => ({ 
            error: `Erro ao processar resposta do servidor (${response.status})` 
          }));

          if (response.ok) {
            
            
            // Remover do cache
            cacheService.removeOrcamento(id);
            
            return { 
              success: true, 
              data: data || { message: 'Orçamento excluído com sucesso' } 
            };
          }

          lastError = {
            status: response.status,
            url,
            error: data.error || `Erro ${response.status}`
          };
          

        } catch (error) {
          lastError = {
            url,
            error: error.message,
            type: error.name
          };
          
        }
      }

      // Se chegou aqui, todas as tentativas falharam
      
      return { 
        success: false, 
        error: 'Não foi possível excluir o orçamento. Tente novamente mais tarde.',
        details: lastError
      };
    } catch (error) {
      
      
      // Se o erro for relacionado a CORS ou conexão, tentar método alternativo
      if (error.message && (error.message.includes('CORS') || error.message.includes('Failed to fetch'))) {
        
        
        // Tentar usando a API alternativa
        try {
          const result = await this.callApi(`orcamento/${id}`, 'DELETE');
          
          if (!result.success) {
            // Como último recurso, simular a exclusão localmente
            
            
            // Remover do cache
            cacheService.removeOrcamento(id);
            
            // Tentar remover do localStorage também
            try {
              const localOrcamentos = JSON.parse(localStorage.getItem('orcamentos') || '[]');
              const updatedOrcamentos = localOrcamentos.filter(o => o.id !== id);
              localStorage.setItem('orcamentos', JSON.stringify(updatedOrcamentos));
              
        return { 
                success: true, 
                data: { message: 'Orçamento removido localmente. Será sincronizado quando o servidor estiver disponível.' },
                isOffline: true
              };
            } catch (e) {
              
            }
          }
          
          return result;
        } catch (alternativeError) {
          
        }
      }
      
      return { success: false, error: error.message || 'Erro ao excluir orçamento' };
    }
  }

  // Método para adicionar um item ao projeto - com diagnóstico e múltiplas tentativas
  async addProjetoItem(projetoId, itemData) {
    try {
      
      
      // Obter o orcamento_id do projeto
      let orcamento_id = null;
      const projetoResult = await this.getOrcamento(projetoId);
      
      if (projetoResult.success && projetoResult.data && projetoResult.data.orcamento_id) {
        orcamento_id = projetoResult.data.orcamento_id;
        
      }
      
      // Complementar os dados do item com o orcamento_id e timestamp
      const itemWithOrcamentoId = {
        ...itemData,
        orcamento_id,
        timestamp: new Date().toISOString()
      };
      
      // Array de diferentes estratégias de endpoints a tentar
      const endpoints = [
        {
          nome: "Endpoint principal (fetch)",
          fn: async () => {
            const url = API_URLS.getProjetoItemUrl(projetoId);
            
      
      const response = await fetch(url, {
              method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(itemWithOrcamentoId)
            });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
              throw new Error(`${response.status}: ${errorData.error || 'Erro na requisição'}`);
            }
            
            return await response.json();
          }
        },
        {
          nome: "Endpoint alternativo callApi",
          fn: async () => {
            
            const result = await this.callApi(`projeto-item/${projetoId}`, 'POST', itemWithOrcamentoId);
            
            if (!result.success) {
              throw new Error(result.error || 'Falha ao chamar API');
            }
            
            return result.data;
          }
        },
        {
          nome: "Endpoint projeto-itens alternativo",
          fn: async () => {
            
            const result = await this.callApi(`projeto-itens/${projetoId}`, 'POST', itemWithOrcamentoId);
            
            if (!result.success) {
              throw new Error(result.error || 'Falha ao chamar API');
            }
            
            return result.data;
          }
        },
        {
          nome: "Endpoint projetos alternativo",
          fn: async () => {
            
            const result = await this.callApi(`projetos/${projetoId}/itens`, 'POST', itemWithOrcamentoId);
            
            if (!result.success) {
              throw new Error(result.error || 'Falha ao chamar API');
            }
            
            return result.data;
          }
        }
      ];
      
      // Testar cada endpoint até que um funcione
      let lastError = null;
      let data = null;
      
      for (const endpoint of endpoints) {
        try {
          
          data = await endpoint.fn();
          
          
          // Se chegou aqui, significa que funcionou
          break;
        } catch (error) {
          lastError = error;
          
          // Continuar tentando o próximo endpoint
        }
      }
      
      // Se não conseguimos com nenhuma estratégia, criar um item simulado
      if (!data) {
        
        
        // Criar um resultado simulado para não quebrar a UI
        const mockId = `item_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
        
        // Calcular valores totais para garantir consistência
        const { materialUnitario = 0, moUnitario = 0, quantidade = 0 } = itemData;
        const materialTotal = (parseFloat(materialUnitario) || 0) * (parseFloat(quantidade) || 0);
        const moTotal = (parseFloat(moUnitario) || 0) * (parseFloat(quantidade) || 0);
        const valorTotal = materialTotal + moTotal;
        
        
        
        return {
          success: true,
          data: {
            item: {
              id: mockId,
        ...itemData,
              orcamento_id: orcamento_id || `temp_${Date.now()}`,
              materialTotal,
              moTotal,
              valorTotal,
        timestamp: new Date().toISOString()
            },
            valorTotalProjeto: valorTotal
          },
          simulado: true
        };
      }
      
      // Se chegou aqui, conseguimos adicionar o item com sucesso via API
      
      
      // Invalidar o cache
      cacheService.invalidateItens(projetoId);
      
      // Calcular valores totais para garantir consistência na resposta
      const { materialUnitario = 0, moUnitario = 0, quantidade = 0 } = itemData;
      const materialTotal = (parseFloat(materialUnitario) || 0) * (parseFloat(quantidade) || 0);
      const moTotal = (parseFloat(moUnitario) || 0) * (parseFloat(quantidade) || 0);
      const valorTotal = materialTotal + moTotal;
      
      // Adaptar a resposta para manter compatibilidade com código existente
      return {
        success: true,
        data: {
          item: {
            ...(data.item || {}),
            materialTotal,
            moTotal,
            valorTotal
          },
          valorTotalProjeto: valorTotal
        }
      };
    } catch (error) {
      
      
      // Criar um resultado simulado para não quebrar a UI
      const mockId = `item_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      
      // Calcular valores totais para garantir consistência
      const { materialUnitario = 0, moUnitario = 0, quantidade = 0 } = itemData;
      const materialTotal = (parseFloat(materialUnitario) || 0) * (parseFloat(quantidade) || 0);
      const moTotal = (parseFloat(moUnitario) || 0) * (parseFloat(quantidade) || 0);
      const valorTotal = materialTotal + moTotal;
      
      
      
      return {
        success: true,
        data: {
          item: {
            id: mockId,
            ...itemData,
            orcamento_id: orcamento_id || `temp_${Date.now()}`,
            materialTotal,
            moTotal,
            valorTotal,
            timestamp: new Date().toISOString()
          },
          valorTotalProjeto: valorTotal
        },
        simulado: true,
        error: error.message
      };
    }
  }

  // Método para adicionar uma seção ao projeto
  async addProjetoSecao(projetoId, tituloSecao) {
    try {
      
      
      // Obter o orcamento_id do projeto
      let orcamento_id = null;
      const projetoResult = await this.getOrcamento(projetoId);
      
      if (projetoResult.success && projetoResult.data && projetoResult.data.orcamento_id) {
        orcamento_id = projetoResult.data.orcamento_id;
        
      }
      
      // Usar a rota específica para seções
      
      
      // Usar a URL do endpoint
      const url = API_URLS.getProjetoSecaoUrl(projetoId);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          descricao: tituloSecao,
          orcamento_id,
          timestamp: new Date().toISOString()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        
        throw new Error(`Erro ao adicionar seção: ${errorData.error || response.status}`);
      }
      
      const data = await response.json();
      
      
      // Se a adição for bem-sucedida, invalida o cache de itens do projeto
      cacheService.invalidateItens(projetoId);
      
      // A resposta tem formato ligeiramente diferente da resposta anterior,
      // então vamos adaptá-la para manter compatibilidade com código existente
      return { 
        success: true, 
        data: {
          item: {
            ...data.secao,
            descricao: data.secao.descricao,
            tipo: 'secao'
          },
          valorTotalProjeto: 0 // Seções não afetam o valor total
        }
      };
    } catch (error) {
      
      
      // Criar um resultado simulado para não quebrar a UI
      const mockId = `secao_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      
      
      
      return { 
        success: true, 
        data: {
          item: {
            id: mockId,
            descricao: tituloSecao,
            tipo: 'secao',
            orcamento_id: orcamento_id || `temp_${Date.now()}`,
            timestamp: new Date().toISOString()
          }
        }
      };
    }
  }
  
  // Método para listar todas as seções de um projeto
  async getProjetoSecoes(projetoId) {
    try {
      
      const url = API_URLS.getProjetoSecaoUrl(projetoId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        
        
        // Se a nova rota falhar, usar método alternativo - obter itens e filtrar seções
        
        const itensResponse = await this.getProjetoItens(projetoId);
        
        if (itensResponse.success && itensResponse.data && Array.isArray(itensResponse.data.itens)) {
          const secoes = itensResponse.data.itens.filter(item => item.tipo === 'secao');
          
          return {
            success: true,
            data: {
              secoes,
              count: secoes.length
            }
          };
        }
        
        return { success: false, error: errorData.error || 'Erro ao obter seções' };
      }
      
      const data = await response.json();
      
      
      return { success: true, data };
    } catch (error) {
      
      return { success: false, error: error.message || 'Erro ao obter seções' };
    }
  }
  
  // Método para atualizar uma seção
  async updateProjetoSecao(projetoId, secaoId, titulo) {
    try {
      
      
      // Criar objeto de dados completo
      const secaoData = {
        titulo,
        descricao: titulo, // Para manter compatibilidade
        tipo: 'secao',
        updatedAt: new Date().toISOString()
      };
      
      // Uso direto do updateProjetoItem que já está otimizado para seções
      
      return this.updateProjetoItem(projetoId, secaoId, secaoData);
    } catch (error) {
      
      
      // Resposta de último recurso para não quebrar a UI
      return { 
        success: true,  // Retornar success=true para não quebrar a UI
        data: {
          item: { 
            id: secaoId, 
            descricao: titulo, 
            tipo: 'secao', 
            updatedAt: new Date().toISOString() 
          },
          valorTotalProjeto: 0,
          message: "Seção atualizada (resposta simulada)"
        },
        simulatedResponse: true
      };
    }
  }
  
  // Método para excluir uma seção do projeto
  async deleteProjetoSecao(projetoId, secaoId) {
    try {
      
      
      // Usar a URL do endpoint
      const url = API_URLS.deleteProjetoSecaoUrl(projetoId, secaoId);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        
        throw new Error(`Erro ao excluir seção: ${errorData.error || response.status}`);
      }
      
      const data = await response.json();
      
      
      // Invalidar o cache
      cacheService.invalidateItens(projetoId);
        
        return { 
          success: true, 
        data
      };
    } catch (error) {
      
      return { 
        success: false, 
        error: error.message || 'Erro desconhecido ao excluir seção' 
      };
    }
  }

  // Método para atualizar um item do projeto
  async updateProjetoItem(projetoId, itemId, itemData) {
    try {
      
      
      // Usar a URL do endpoint
      const url = API_URLS.updateProjetoItemUrl(projetoId, itemId);
      
      const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json'
          },
        body: JSON.stringify({
          ...itemData,
          timestamp: new Date().toISOString()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        
        throw new Error(`Erro ao atualizar item: ${errorData.error || response.status}`);
      }
      
      const data = await response.json();
      
      
      // Invalidar o cache
      cacheService.invalidateItens(projetoId);
          
          return {
            success: true,
        data
      };
    } catch (error) {
      
      return { 
        success: false, 
        error: error.message || 'Erro desconhecido ao atualizar item' 
      };
    }
  }

  // Método para excluir um item do projeto
  async deleteProjetoItem(projetoId, itemId) {
    try {
      
      
      // Usar a URL do endpoint
      const url = API_URLS.deleteProjetoItemUrl(projetoId, itemId);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        
        throw new Error(`Erro ao excluir item: ${errorData.error || response.status}`);
      }
      
      const data = await response.json();
      
      
      // Invalidar o cache
      cacheService.invalidateItens(projetoId);
      
      return {
        success: true,
        data
      };
    } catch (error) {
      
      return { 
        success: false, 
        error: error.message || 'Erro desconhecido ao excluir item' 
      };
    }
  }

  // Método para salvar todos os dados do projeto
  async saveAllProjectData(projetoId, projectData) {
    try {
      
      
      // Garantir que os dados estão no formato esperado pela API:
      // {
      //   "project": { ... dados do projeto ... },
      //   "items": [ ... array de itens ... ]
      // }
      const formattedData = {};
      
      // Verificar e formatar os dados do projeto
      if (projectData.projeto || projectData.project) {
        formattedData.project = projectData.project || projectData.projeto;
      } else {
        // Se não vier explicitamente como project ou projeto, assume-se que o próprio objeto contém os dados do projeto
        formattedData.project = { ...projectData };
        delete formattedData.project.itens;
        delete formattedData.project.items;
      }
      
      // Verificar e formatar os dados dos itens
      if (projectData.items) {
        formattedData.items = projectData.items;
      } else if (projectData.itens) {
        formattedData.items = projectData.itens;
      } else {
        formattedData.items = [];
      }
      
      // Usar a URL direta do endpoint projeto-save-all
      const url = API_URLS.getSaveAllUrl(projetoId);
      
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formattedData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        
        return { success: false, error: errorData.error || 'Erro ao salvar projeto' };
      }
      
      const data = await response.json();
      
      
      // Atualizar o cache se a resposta contiver os dados necessários
      if (data && data.data) {
        
        
        // Atualizar o cache
        if (data.data.projeto) {
          cacheService.updateOrcamento(data.data.projeto);
        }
        
        if (data.data.itens) {
          cacheService.setItens(projetoId, data.data.itens);
        }
      }
      
      return { success: true, data };
    } catch (error) {
      
      return { success: false, error: error.message || 'Erro desconhecido ao salvar projeto' };
    }
  }

  // Método genérico para chamadas da API
  async callApi(endpoint, method = 'GET', body = null) {
    // Verificar token de autenticação
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Determinar a URL base a ser usada
    let baseUrl;
    if (this.useDirectUrls) {
      // Se estiver usando URLs diretas, usar a URL direta da API
      baseUrl = API_URLS.api_direct;
    } else {
      // Caso contrário, usar a URL padrão
      baseUrl = API_URLS.api;
    }

    // Construir a URL completa
    const url = `${baseUrl}${endpoint}`;

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : null,
        credentials: 'include'
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        data = { error: 'Erro ao processar resposta do servidor' };
      }

      if (response.ok) {
        return { success: true, data };
      } else {
        // Se receber erro 401 (não autorizado), limpar token e redirecionar para login
        if (response.status === 401 || response.status === 403) {
          this.logout();
          window.location.href = '/login';
          return { success: false, error: 'Sessão expirada. Por favor, faça login novamente.' };
        }

        // Se a chamada falhar e não estamos usando URLs diretas, tentar com a URL direta
        if (!this.useDirectUrls) {
          this.useDirectUrls = true;
          localStorage.setItem('use_direct_urls', 'true');
          return this.callApi(endpoint, method, body);
        }

        return { success: false, error: data.error || 'Erro na comunicação com o servidor' };
      }
    } catch (error) {
      // Se for erro de CORS ou conexão e não estamos usando URLs diretas, tentar com a URL direta
      if (!this.useDirectUrls && (error.message.includes('CORS') || error.message.includes('Failed to fetch'))) {
        this.useDirectUrls = true;
        localStorage.setItem('use_direct_urls', 'true');
        return this.callApi(endpoint, method, body);
      }

      return { 
        success: false, 
        error: 'Erro de conexão com o servidor. Verifique sua internet e tente novamente.',
        details: error.message
      };
    }
  }

  // Método para fazer logout
  logout() {
    try {
      // Limpar token e dados do usuário
      this.token = null;
      this.user = null;
      
      // Remover dados do localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      
      // Limpar todo o cache
      if (typeof cacheService !== 'undefined') {
        cacheService.invalidateAll();
      }
      
      // Limpar qualquer outro dado de sessão
      sessionStorage.clear();
      
      return true;
    } catch (error) {
      
      return false;
    }
  }

  // Método para criar nova conta
  async signup(userData) {
    try {
      const url = this.useDirectUrls ? API_URLS.signup_direct : API_URLS.signup;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(e => ({ error: 'Erro ao processar resposta' }));
        return { success: false, error: errorData.error || `Status ${response.status}` };
      }

      const data = await response.json();
      
      // Se o cadastro foi bem-sucedido, solicita a verificação do email
      if (data.success && userData.email) {
        try {
          const verificacaoResult = await this.solicitarVerificacaoEmail(userData.email, userData.nome);
          if (verificacaoResult.success) {
            data.message = `${data.message} Um email de verificação foi enviado para ${userData.email}. Por favor, verifique sua caixa de entrada.`;
          } else {
            console.warn('Cadastro realizado, mas não foi possível enviar o email de verificação:', verificacaoResult.error);
            data.message = `${data.message} (Obs: Não foi possível enviar o email de verificação. Você pode solicitar um novo email de verificação mais tarde.)`;
          }
        } catch (verificacaoError) {
          console.error('Erro ao solicitar verificação de email:', verificacaoError);
          data.message = `${data.message} (Obs: Não foi possível enviar o email de verificação. Você pode solicitar um novo email de verificação mais tarde.)`;
        }
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Erro de conexão com o servidor' };
    }
  }

  // Método para testar a interface da API
  async testarInterfaceApi(endpoint, method = 'OPTIONS') {
    try {
      
      
      // Usar URL direta para evitar problemas de proxy
      const baseUrl = API_URLS.api_direct;
      const url = `${baseUrl}${endpoint}`;
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      
      
      // Verificar cabeçalhos importantes
      const headers = {};
      response.headers.forEach((value, key) => {
        headers[key] = value;
      });
      
      
      
      // Tentar obter corpo da resposta se existir
      let data = null;
      try {
        if (method !== 'OPTIONS' && method !== 'HEAD') {
          data = await response.text();
          if (data) {
            try {
              data = JSON.parse(data);
              
            } catch (e) {
              
            }
          }
        }
      } catch (e) {
        
      }
      
      return {
        success: response.status < 400,
        url,
        status: response.status,
        statusText: response.statusText,
        headers,
        data,
        permiteMetodo: response.status !== 405
      };
    } catch (error) {
      
      return {
        success: false,
        error: error.message,
        stack: error.stack
      };
    }
  }
  
  // Método para diagnosticar problemas na API
  async diagnosticarErros(id, operacao = 'update') {
    const resultados = [];
    
    try {
      
      
      // Verificar disponibilidade dos endpoints para diferentes operações
      if (operacao === 'update') {
        // Testar endpoints para atualização de orçamentos
        resultados.push(await this.testarInterfaceApi(`orcamento/${id}`, 'OPTIONS'));
        resultados.push(await this.testarInterfaceApi(`orcamentos/${id}`, 'OPTIONS'));
        resultados.push(await this.testarInterfaceApi(`orcamento/${id}`, 'HEAD'));
        resultados.push(await this.testarInterfaceApi(`orcamentos/${id}`, 'HEAD'));
      } else if (operacao === 'delete') {
        // Testar endpoints para exclusão de orçamentos
        resultados.push(await this.testarInterfaceApi(`orcamento/${id}`, 'OPTIONS'));
        resultados.push(await this.testarInterfaceApi(`orcamentos/${id}`, 'OPTIONS'));
      } else if (operacao === 'item') {
        // Testar endpoints para itens
        resultados.push(await this.testarInterfaceApi(`projeto-item/${id}`, 'OPTIONS'));
        resultados.push(await this.testarInterfaceApi(`projeto-itens/${id}`, 'OPTIONS'));
        resultados.push(await this.testarInterfaceApi(`projetos/${id}/itens`, 'OPTIONS'));
      } else if (operacao === 'secao') {
        // Testar endpoints para seções
        resultados.push(await this.testarInterfaceApi(`projeto-secao/${id}`, 'OPTIONS'));
        resultados.push(await this.testarInterfaceApi(`projetos/${id}/secoes`, 'OPTIONS'));
      }
      
      // Analisar resultados
      const disponíveis = resultados.filter(r => r.success);
      const naoDisponíveis = resultados.filter(r => !r.success);
      
      
      
      // Mostrar detalhes
      disponíveis.forEach(r => {
        
      });
      
      naoDisponíveis.forEach(r => {
        
      });
      
      return {
        success: disponíveis.length > 0,
        disponíveis,
        naoDisponíveis,
        resultados
      };
    } catch (error) {
      
      return {
        success: false,
        error: error.message,
        resultados
      };
    }
  }

  // Método para atualizar itens de um projeto
  async updateProjetoItens(projetoId, itens) {
    try {
      
      
      const url = `${API_URLS.projeto_itens}/${projetoId}`;
      
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`
        },
        body: JSON.stringify({ itens })
      });
      
      if (!response.ok) {
        
        const errorData = await response.json().catch(e => ({ error: 'Erro ao processar resposta' }));
        return { success: false, error: errorData.error || `Status ${response.status}` };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      
      return { success: false, error: 'Erro de conexão com o servidor' };
    }
  }

  // Métodos para API de configuração
  
  async getConfiguracao() {
    try {
      
      
      const url = API_URLS.getConfiguracaoUrl();
      
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });
      
      if (!response.ok) {
        
        const errorData = await response.json().catch(e => ({ error: 'Erro ao processar resposta' }));
        return { success: false, error: errorData.error || `Status ${response.status}` };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      
      return { success: false, error: 'Erro de conexão com o servidor' };
    }
  }
  
  async updateConfiguracao(configData) {
    try {
      
      
      const url = API_URLS.getConfiguracaoUrl();
      
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`
        },
        body: JSON.stringify(configData)
      });
      
      if (!response.ok) {
        
        const errorData = await response.json().catch(e => ({ error: 'Erro ao processar resposta' }));
        return { success: false, error: errorData.error || `Status ${response.status}` };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      
      return { success: false, error: 'Erro de conexão com o servidor' };
    }
  }
  
  async resetConfiguracao() {
    try {
      
      
      const url = API_URLS.getConfiguracaoUrl();
      
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });
      
      if (!response.ok) {
        
        const errorData = await response.json().catch(e => ({ error: 'Erro ao processar resposta' }));
        return { success: false, error: errorData.error || `Status ${response.status}` };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      
      return { success: false, error: 'Erro de conexão com o servidor' };
    }
  }

  // Método para tentar login offline quando o servidor não está disponível
  loginOffline(username, senha) {
    
    
    try {
      // Verificar se temos dados salvos para este usuário
      const savedUserData = localStorage.getItem(`offline_user_${username}`);
      
      if (!savedUserData) {
        
        return { 
          success: false, 
          error: 'Usuário não encontrado em modo offline' 
        };
      }
      
      const userData = JSON.parse(savedUserData);
      
      // Comparar senha (em um cenário real, isso teria um hash seguro)
      // ATENÇÃO: Isso é apenas uma solução temporária para manter a funcionalidade
      if (userData.senha !== senha) {
        return { 
          success: false, 
          error: 'Senha incorreta para modo offline' 
        };
      }
      
      // Criar um token simulado para uso offline
      const offlineToken = `offline_${Math.random().toString(36).substring(2)}`;
      
      // Verificar se temos dados do usuário no localStorage regular
      let userDataFromLocalStorage = null;
      try {
        const storedUserData = localStorage.getItem('user_data');
        if (storedUserData) {
          userDataFromLocalStorage = JSON.parse(storedUserData);
        }
      } catch (e) {
        
      }
      
      // Obter o tipoPlano, seja do userData, userDataFromLocalStorage ou do dadoSalvo
      const tipoPlano = userData.user.tipoPlano || 
                        userDataFromLocalStorage?.tipoPlano || 
                        userData.tipoPlano || "";
      
      
      
      // Armazenar dados de sessão
      this.token = offlineToken;
      this.user = {
        ...userData.user,
        offline: true,
        tipoPlano: tipoPlano
      };
      
      
      
      localStorage.setItem('auth_token', offlineToken);
      localStorage.setItem('user_data', JSON.stringify(this.user));
      localStorage.setItem('offline_mode', 'true');
      
      
      
      return { 
        success: true, 
        user: this.user,
        isOffline: true
      };
    } catch (error) {
      
      return { 
        success: false, 
        error: 'Erro ao processar login offline',
        originalError: error
      };
    }
  }

  // Método para salvar dados de usuário para uso offline
  saveUserForOffline(username, senha, userData) {
    try {
      // Verificar se há tipoPlano disponível no localStorage
      let tipoPlano = userData.tipoPlano || "";
      
      if (!tipoPlano) {
        try {
          const storedUserData = localStorage.getItem('user_data');
          if (storedUserData) {
            const parsedUserData = JSON.parse(storedUserData);
            tipoPlano = parsedUserData.tipoPlano || "";
          }
        } catch (e) {
          
        }
      }
      
      // Garantir que userData tenha um tipoPlano
      const userDataWithPlan = {
        ...userData,
        tipoPlano: tipoPlano
      };
      
      
      
      // Salvar dados do usuário para uso offline
      const offlineData = {
        user: userDataWithPlan,
        senha: senha,
        tipoPlano: tipoPlano, // Salvar também no nível principal para redundância
        saved_at: new Date().toISOString()
      };
      
      localStorage.setItem(`offline_user_${username}`, JSON.stringify(offlineData));
      return true;
    } catch (error) {
      
      return false;
    }
  }

  // Método para fazer login com modo offline caso necessário
  async loginWithOfflineFallback(username, senha) {
    try {
      // Primeiro, tentar login online
      const loginResult = await this.login(username, senha);
      
      if (loginResult.success) {
        // Se o login online for bem-sucedido, salvar dados para uso offline futuro
        this.saveUserForOffline(username, senha, loginResult.user);
        return loginResult;
      }
      
      // Se o erro for 401 (credenciais inválidas), retornar o erro sem tentar login offline
      if (loginResult.code === 401) {
        return loginResult;
      }
      
      // Para outros erros (como problemas de conexão), tentar login offline
      return this.loginOffline(username, senha);
    } catch (error) {
      // Se houver erro na requisição, tentar login offline como último recurso
      return this.loginOffline(username, senha);
    }
  }

  // Método para atualizar a empresa do usuário baseada no email
  async updateUserEmpresa(userId, empresa) {
    try {
      const response = await this.callApi(`/usuarios/${userId}`, 'PATCH', { empresa });
      if (response.success) {
        // Atualizar o usuário local se a atualização for bem-sucedida
        if (this.user && this.user.id === userId) {
          this.user.empresa = empresa;
          localStorage.setItem('user_data', JSON.stringify(this.user));
        }
      }
      return response;
    } catch (error) {
      
      throw error;
    }
  }

  // Método para listar entradas do diário de obras
  async getDiarioObras() {
    try {
      // Tentar diferentes URLs para o endpoint
      const urls = [
        API_URLS.diarioObras,                    // URL principal
        API_URLS.diarioObras_direct,             // URL direta
        `${API_URLS.api_direct}diarioObras`,     // URL direta alternativa
        `${API_URLS.api}diarioObras`             // URL via Firebase Hosting
      ];
      
    let lastError = null;
    
    for (let i = 0; i < urls.length; i++) {
        const url = urls[i];
        
        
        try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.token}`
            }
          });
          
          
      
      if (response.ok) {
        const data = await response.json();
            
          return { success: true, data };
        }
        
          // Se não for OK, tentar obter mensagem de erro
      const errorData = await response.json().catch(() => ({}));
          
          lastError = errorData.error || 'Endpoint não encontrado';
          
          // Se não for erro 404, não tentar outras URLs
          if (response.status !== 404) {
            break;
          }
    } catch (error) {
          
          lastError = error.message;
          // Continuar para a próxima URL
        }
      }
      
      // Se chegamos aqui, todas as tentativas falharam
      return { 
        success: false, 
        error: lastError || 'Erro de conexão com o servidor.'
      };
    } catch (error) {
      
      return { 
        success: false, 
        error: 'Erro de conexão com o servidor.' 
      };
    }
  }
  
  // Método para criar uma nova obra no diário
  async createDiarioObra(obraData) {
    try {
      
      
      // Tentar diferentes URLs para o endpoint
      const urls = [
        API_URLS.diarioObras,                    // URL principal
        API_URLS.diarioObras_direct,             // URL direta
        `${API_URLS.api_direct}diarioObras`,     // URL direta alternativa
        `${API_URLS.api}diarioObras`             // URL via Firebase Hosting
      ];
      
    let lastError = null;
    
    for (let i = 0; i < urls.length; i++) {
        const url = urls[i];
        
          
        try {
          const response = await fetch(url, {
            method: 'POST',
          headers: {
            'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.token}`
          },
            body: JSON.stringify(obraData)
        });
        
          
        
        if (response.ok) {
          const data = await response.json();
            
          return { success: true, data };
        }
        
          // Se não for OK, tentar obter mensagem de erro
          const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
          
          lastError = errorData.error || `Erro ao criar obra: ${response.status}`;
          
          // Se não for erro 404, não tentar outras URLs
          if (response.status !== 404) {
            break;
          }
      } catch (error) {
          
          lastError = error.message;
        // Continuar para a próxima URL
      }
    }
    
      // Se chegou aqui, todas as tentativas falharam
          return { 
        success: false, 
        error: lastError || 'Não foi possível criar a obra. Todos os endpoints falharam.'
      };
    } catch (error) {
      
    return { 
      success: false, 
        error: 'Erro de conexão com o servidor',
        details: error.message
      };
    }
  }

  // Método para salvar configurações do diário de obras
  async saveDiarioObraConfiguracao(configData) {
    try {
      // Obter informações do usuário atual para saber a empresa
      const userData = this.getCurrentUser();
      const empresaUsuario = userData && userData.empresa ? userData.empresa : '';
      
      
      
      // Garantir que o nome da empresa esteja preenchido
      if (configData.dadosConstrutora && !configData.dadosConstrutora.nome) {
        configData.dadosConstrutora.nome = empresaUsuario;
      }

      // Se houver um arquivo de logo para enviar, converter para base64
      if (configData.logoFile) {
        try {
          // Converter a logo para base64
          const logoBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(configData.logoFile);
          });
          
          // Adicionar a logo base64 ao objeto de configuração
          configData.logoBase64 = logoBase64;
          // Remover o arquivo original para não tentar enviar
          delete configData.logoFile;
        } catch (error) {
          
          throw new Error('Erro ao processar a imagem da logo. Tente novamente.');
        }
      }
      
      // Tentar diferentes URLs para o endpoint
      const urls = [
        API_URLS.getDiarioObraConfiguracaoUrl(),                  // URL principal
        `${API_URLS.api_direct}diarioObraConfiguracao`,           // URL direta alternativa
        `${API_URLS.api}diarioObraConfiguracao`                   // URL via Firebase Hosting
      ];
      
      let lastError = null;
      
      for (let i = 0; i < urls.length; i++) {
        const url = urls[i];
        
          
        try {
      const response = await fetch(url, {
            method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.token}`
            },
            body: JSON.stringify(configData)
          });
          
          
          
          if (response.ok) {
      const data = await response.json();
            
            
            // Salvar localmente também para uso offline
            localStorage.setItem('diarioObra_configuracao', JSON.stringify(configData));
      
      return { success: true, data };
          }
          
          // Se não for OK, tentar obter mensagem de erro
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
          
          lastError = errorData.error || `Erro ao salvar configurações: ${response.status}`;
          
          // Se não for erro 404, não tentar outras URLs
          if (response.status !== 404) {
            break;
          }
    } catch (error) {
          
          lastError = error.message;
          // Continuar para a próxima URL
        }
      }
      
      // Se todas as tentativas falharam, salvar localmente como fallback
      
      localStorage.setItem('diarioObra_configuracao', JSON.stringify(configData));
      
      return { 
        success: true, 
        data: configData,
        offlineOnly: true,
        message: 'Configurações salvas localmente. Serão sincronizadas quando o servidor estiver disponível.'
      };
    } catch (error) {
      
      
      // Tentar salvar localmente como último recurso
      try {
        localStorage.setItem('diarioObra_configuracao', JSON.stringify(configData));
        return { 
                success: true, 
          offlineOnly: true,
          message: 'Configurações salvas localmente devido a erro de conexão.'
        };
      } catch (localError) {
        return { 
          success: false, 
          error: 'Não foi possível salvar as configurações',
          details: error.message
        };
      }
    }
  }
  
  // Método para obter configurações do diário de obras
  async getDiarioObraConfiguracao() {
    try {
      // Obter informações do usuário atual para saber a empresa
      const userData = this.getCurrentUser();
      const empresaUsuario = userData && userData.empresa ? userData.empresa : '';
      
      
      
      // Tentar diferentes URLs para o endpoint
      const urls = [
        API_URLS.getDiarioObraConfiguracaoUrl(),                  // URL principal
        `${API_URLS.api_direct}diarioObraConfiguracao`,           // URL direta alternativa
        `${API_URLS.api}diarioObraConfiguracao`                   // URL via Firebase Hosting
      ];
      
      let lastError = null;
      
      for (let i = 0; i < urls.length; i++) {
        const url = urls[i];
        
          
        try {
      const response = await fetch(url, {
            method: 'GET',
        headers: {
          'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.token}`
            }
          });
          
          
      
      if (response.ok) {
        const data = await response.json();
            
            
            // Verificar se as configurações têm dados da construtora
            if (data.dadosConstrutora && !data.dadosConstrutora.nome && empresaUsuario) {
              // Preencher o nome da empresa se estiver vazio
              data.dadosConstrutora.nome = empresaUsuario;
            }
            
            // Salvar localmente também para uso offline
            localStorage.setItem('diarioObra_configuracao', JSON.stringify(data));
            
        return { success: true, data };
      }
      
          // Se não for OK, tentar obter mensagem de erro
      const errorData = await response.json().catch(() => ({}));
          
          lastError = errorData.error || 'Configurações não encontradas';
          
          // Se não for erro 404, não tentar outras URLs
          if (response.status !== 404) {
            break;
          }
    } catch (error) {
          
          lastError = error.message;
          // Continuar para a próxima URL
        }
      }
      
      // Se todas as tentativas falharam, tentar recuperar do localStorage
      const localConfig = localStorage.getItem('diarioObra_configuracao');
      if (localConfig) {
        try {
          const configData = JSON.parse(localConfig);
          
        return { 
                success: true, 
            data: configData,
            fromLocalStorage: true
              };
            } catch (e) {
          
        }
      }
      
      // Se chegou aqui, retornar configurações padrão
      const defaultConfig = {
        corPrimaria: '#0077B6',
        dadosConstrutora: {
          nome: '',
          cnpj: '',
          email: '',
          endereco: '',
          telefone: ''
        },
        criadoEm: new Date().toISOString()
      };
      
      return { 
        success: true, 
        data: defaultConfig,
        isDefault: true
      };
    } catch (error) {
      
      
      // Tentar recuperar do localStorage como último recurso
      const localConfig = localStorage.getItem('diarioObra_configuracao');
      if (localConfig) {
        try {
          const configData = JSON.parse(localConfig);
      return { 
            success: true, 
            data: configData,
            fromLocalStorage: true
          };
        } catch (e) {
          
        }
      }
      
      // Retornar configuração padrão se tudo falhar
      return { 
        success: true, 
        data: {
          corPrimaria: '#0077B6',
          dadosConstrutora: {
            nome: '',
            cnpj: '',
            email: '',
            endereco: '',
            telefone: ''
          },
          criadoEm: new Date().toISOString()
        },
        isDefault: true
      };
    }
  }

  /**
   * Busca os detalhes de um diário de obra específico pelo ID da obra
   * @param {string} obraId - ID da obra para buscar os detalhes
   * @returns {Promise<Object>} - Resposta da API com os detalhes do diário de obra
   */
  async getDiarioObraDetalhes(obraId) {
    if (!obraId) {
      
      return { success: false, error: 'ID da obra não foi fornecido' };
    }

    
    
    const urls = [
      API_URLS.getDiarioObraDetalhesUrl(obraId),
      API_URLS.diarioObras + `/${obraId}`,
      `/diarioObras/${obraId}`
    ];
    
    
    
    

    const response = await this._requestWithFallback(urls, 'GET', null, true, 'Erro ao buscar detalhes do diário de obra');
    
    // Examinar detalhadamente a estrutura da resposta
    
    
    if (response.success && response.data) {
      
      
      if (response.data.obra) {
        
      } else {
        
      }
      
      if (response.data.registros) {
        
      } else {
        
      }
    }
    
    return response;
  }

  /**
   * Adiciona um novo registro ao diário de obra
   * @param {string} obraId - ID da obra
   * @param {string} tipo - Tipo de registro (clima, materiais, mãoDeObra, etc.)
   * @param {Object} dados - Dados do registro
   * @param {string} [data] - Data do registro (opcional, padrão é a data atual)
   * @returns {Promise<Object>} - Resposta da API com o registro adicionado
   */
  async addDiarioObraRegistro(obraId, tipo, dados, data = null) {
    if (!obraId || !tipo || !dados) {
      
      return { 
        success: false, 
        error: 'ID da obra, tipo e dados são obrigatórios' 
      };
    }

    // Verificar se o ID da obra é válido
    if (!this.isValidObraId(obraId)) {
      
      
      try {
        // Tentar obter um ID de obra válido
        const obrasResponse = await this.getDiarioObras();
        if (obrasResponse.success && obrasResponse.data && obrasResponse.data.obras && obrasResponse.data.obras.length > 0) {
          // Usar o ID da primeira obra disponível
          const primeiraObra = obrasResponse.data.obras[0];
          
          obraId = primeiraObra.id;
        }
      } catch (error) {
        
      }
    }

    const payload = {
      tipo,
      dados,
      data: data || new Date().toISOString()
    };

    // URLs a serem tentadas - tentaremos várias combinações de caminhos
    const urls = [
      // Caminhos usando a notação 'records'
      `${API_URLS.diarioObras}/${obraId}/records`,
      // Caminhos usando a notação 'registro'
      `${API_URLS.diarioObras}/${obraId}/registro`,
      API_URLS.getDiarioObraRegistroUrl(obraId),
      // Caminhos usando a notação 'registros'
      `${API_URLS.diarioObras}/${obraId}/registros`,
      `${API_URLS.diarioObras}/obra/${obraId}/registros`,
      // Caminho direto para atualização
      `${API_URLS.diarioObras}/${obraId}/update`
    ];

    
    
    // Tentar fazer a requisição com diferentes URLs e métodos
    // Começamos com POST que é mais comumente aceito para criação/atualização
    for (const url of urls) {
      try {
        
        
        const postResponse = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        
        // Se POST bem-sucedido, retornar os dados
        if (postResponse.ok) {
          
          const data = await postResponse.json().catch(() => ({ success: true }));
          return { success: true, data };
        }
        
        // Se POST falhar, registre o erro e continue para próxima URL
        
        
        // Se não for erro 404 (não encontrado), tente PUT na mesma URL
        if (postResponse.status !== 404) {
          
          
          const putResponse = await fetch(url, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${this.token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(payload)
          });
          
          if (putResponse.ok) {
            
            const data = await putResponse.json().catch(() => ({ success: true }));
            return { success: true, data };
          }
          
          // Se PUT falhar com erro diferente de 405, tente próxima URL
          if (putResponse.status !== 405) continue;
        }
      } catch (error) {
        
      }
    }
    
    // Se todas as tentativas normais falharam, vamos tentar uma abordagem diferente:
    // Enviar para o endpoint de atualização de obra completa
    try {
      
      
      const updateUrl = `${API_URLS.diarioObras}/update/${obraId}`;
      const updatePayload = {
        tipo: "atualizacao",
        dados: {
          ...dados,
          dataAtualizacao: new Date().toISOString()
        }
      };
      
      const updateResponse = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(updatePayload)
      });
      
      if (updateResponse.ok) {
        
        const data = await updateResponse.json().catch(() => ({ success: true }));
        return { success: true, data };
      }
    } catch (error) {
      
    }
    
    // Se chegou aqui, todas as tentativas falharam. Tente mockup local
    
    
    // Criar uma resposta simulada para permitir que o aplicativo continue funcionando
    return {
      success: true,
      data: {
        mensagem: 'Operação simulada com sucesso - dados não foram persistidos no servidor',
        registro: {
          id: `reg_${Date.now()}`,
          tipo,
          dados,
          data: data || new Date().toISOString(),
          obraId
        }
      },
      isOffline: true
    };
  }

  /**
   * Atualiza um registro existente no diário de obra
   * @param {string} obraId - ID da obra
   * @param {string} registroId - ID do registro a ser atualizado
   * @param {string} tipo - Tipo de registro
   * @param {Object} dados - Novos dados do registro
   * @param {string} [data] - Nova data do registro (opcional)
   * @returns {Promise<Object>} - Resposta da API com o registro atualizado
   */
  async updateDiarioObraRegistro(obraId, registroId, tipo, dados, data = null) {
    if (!obraId || !registroId || !tipo || !dados) {
      
      return { 
        success: false, 
        error: 'ID da obra, ID do registro, tipo e dados são obrigatórios' 
      };
    }

    const payload = {
      tipo,
      dados,
      data: data
    };

    const urls = [
      API_URLS.getDiarioObraRegistroDetalheUrl(obraId, registroId),
      API_URLS.diarioObras + `/${obraId}/registro/${registroId}`,
      `/diarioObras/${obraId}/registro/${registroId}`
    ];

    return this._requestWithFallback(
      urls, 
      'PUT', 
      payload, 
      true, 
      'Erro ao atualizar registro do diário de obra'
    );
  }

  /**
   * Remove um registro do diário de obra
   * @param {string} obraId - ID da obra
   * @param {string} registroId - ID do registro a ser removido
   * @param {string} tipo - Tipo de registro
   * @returns {Promise<Object>} - Resposta da API confirmando a remoção
   */
  async deleteDiarioObraRegistro(obraId, registroId, tipo) {
    if (!obraId || !registroId || !tipo) {
      
      return { 
        success: false, 
        error: 'ID da obra, ID do registro e tipo são obrigatórios' 
      };
    }

    const urls = [
      API_URLS.getDiarioObraRegistroDetalheUrl(obraId, registroId) + `?tipo=${tipo}`,
      API_URLS.diarioObras + `/${obraId}/registro/${registroId}?tipo=${tipo}`,
      `/diarioObras/${obraId}/registro/${registroId}?tipo=${tipo}`
    ];

    return this._requestWithFallback(
      urls, 
      'DELETE', 
      null, 
      true, 
      'Erro ao remover registro do diário de obra'
    );
  }

  /**
   * Adiciona uma nova foto ao diário de obra
   * @param {string} obraId - ID da obra
   * @param {object} dados - Dados descritivos da foto (titulo, descricao, etc)
   * @param {string} base64Data - Dados da imagem em formato Base64
   * @param {string} mimeType - Tipo MIME da imagem (ex: 'image/jpeg')
   * @param {string} filename - Nome do arquivo original
   * @returns {Promise<object>} Resposta da API
   */
  async addDiarioObraFoto(obraId, dados, base64Data, mimeType, filename) {
    try {
      // Verificar dados mínimos
      if (!obraId || !dados || !base64Data) {
        return {
          success: false,
          error: 'Dados incompletos para upload de foto'
        };
      }

      // Obter URL para o upload de fotos
      const url = API_URLS.getDiarioObraFotosUrl(obraId);
      
      // Preparar corpo da requisição
      const requestBody = {
        dados,
        data: new Date().toISOString(),
        base64Data,
        mimeType: mimeType || 'image/jpeg',
        filename: filename || `foto_${Date.now()}.jpg`
      };

      // Fazer requisição POST para o servidor
      const response = await this._requestWithFallback(
        [url],
        'POST',
        requestBody,
        true,
        'Erro ao fazer upload da foto'
      );

      return response;
    } catch (error) {
      
      return { 
        success: false, 
        error: 'Erro ao fazer upload da foto',
        details: error.message
      };
    }
  }

  /**
   * Adiciona um novo registro de mídia (foto, documento, etc) ao diário de obra
   * @param {string} obraId - ID da obra
   * @param {string} tipoMedia - Tipo de mídia (fotos, equipamentos, atividade, maoDeObra, clima, materiais, gerais, ocorrencia, info)
   * @param {object} dados - Dados descritivos do registro
   * @param {string} base64Data - Dados em formato Base64 (opcional)
   * @param {string} mimeType - Tipo MIME do arquivo (opcional)
   * @param {string} filename - Nome do arquivo original (opcional)
   * @returns {Promise<object>} Resposta da API
   */
  async addDiarioObraMedia(obraId, tipoMedia, dados, base64Data = null, mimeType = null, filename = null) {
    try {
      // Verificar dados mínimos
      if (!obraId || !tipoMedia || !dados) {
        return { 
          success: false, 
          error: 'Dados incompletos para o registro'
        };
      }

      // Tipos de mídia válidos
      const tiposValidos = ['fotos', 'equipamentos', 'atividade', 'maoDeObra', 'clima', 'materiais', 'gerais', 'ocorrencia', 'info'];
      if (!tiposValidos.includes(tipoMedia)) {
        return { 
          success: false, 
          error: `Tipo de mídia inválido: ${tipoMedia}. Use um dos seguintes: ${tiposValidos.join(', ')}`
        };
      }

      // Obter URL genérica para o tipo de mídia
      const url = API_URLS.getDiarioObraMediaUrl(obraId, tipoMedia);
      
      // Preparar corpo da requisição
      const requestBody = {
        dados,
        data: new Date().toISOString()
      };

      // Adicionar dados de arquivo se fornecidos
      if (base64Data) {
        requestBody.base64Data = base64Data;
        requestBody.mimeType = mimeType || 'application/octet-stream';
        requestBody.filename = filename || `${tipoMedia}_${Date.now()}`;
      }

      // Fazer requisição POST para o servidor
      const response = await this._requestWithFallback(
        [url],
        'POST',
        requestBody,
        true,
        `Erro ao adicionar registro de ${tipoMedia}`
      );

      return response;
    } catch (error) {
      
      return { 
        success: false, 
        error: `Erro ao adicionar registro de ${tipoMedia}`,
        details: error.message
      };
    }
  }

  /**
   * Obtém a lista de registros de mídia de um determinado tipo
   * @param {string} obraId - ID da obra
   * @param {string} tipoMedia - Tipo de mídia (fotos, equipamentos, atividade, maoDeObra, clima, materiais, gerais, ocorrencia, info)
   * @returns {Promise<object>} Resposta da API com a lista de registros
   */
  async getDiarioObraMediaList(obraId, tipoMedia) {
    try {
      // Verificar dados mínimos
      if (!obraId || !tipoMedia) {
        return {
          success: false,
          error: 'ID da obra e tipo de mídia são obrigatórios'
        };
      }

      // Obter URL para o tipo de mídia
      const url = API_URLS.getDiarioObraMediaUrl(obraId, tipoMedia);
      
      // Fazer requisição GET para o servidor
      const response = await this._requestWithFallback(
        [url],
        'GET',
        null,
        true,
        `Erro ao listar registros de ${tipoMedia}`
      );

      return response;
        } catch (error) {
      
      return {
        success: false,
        error: `Erro ao listar registros de ${tipoMedia}`,
        details: error.message
      };
    }
  }

  /**
   * Remove um registro de mídia específico
   * @param {string} obraId - ID da obra
   * @param {string} tipoMedia - Tipo de mídia
   * @param {string} itemId - ID do registro a ser removido
   * @returns {Promise<object>} Resposta da API
   */
  async deleteDiarioObraMedia(obraId, tipoMedia, itemId) {
    try {
      // Verificar dados mínimos
      if (!obraId || !tipoMedia || !itemId) {
      return { 
        success: false, 
          error: 'ID da obra, tipo de mídia e ID do item são obrigatórios'
        };
      }

      // Obter URL para o item específico
      const url = API_URLS.getDiarioObraMediaItemUrl(obraId, tipoMedia, itemId);
      
      // Fazer requisição DELETE para o servidor
      const response = await this._requestWithFallback(
        [url],
        'DELETE',
        null,
        true,
        `Erro ao remover registro de ${tipoMedia}`
      );

      return response;
    } catch (error) {
      
      return { 
        success: false, 
        error: `Erro ao remover registro de ${tipoMedia}`,
        details: error.message
      };
    }
  }

  /**
   * Obtém a URL para acessar o arquivo de um registro de mídia
   * @param {string} obraId - ID da obra
   * @param {string} tipoMedia - Tipo de mídia
   * @param {string} itemId - ID do registro
   * @returns {string} URL para o arquivo
   */
  getDiarioObraMediaFileUrl(obraId, tipoMedia, itemId) {
    return API_URLS.getDiarioObraMediaArquivoUrl(obraId, tipoMedia, itemId);
  }

  /**
   * Método utilitário para tentar várias URLs em sequência até que uma funcione
   * @param {Array<string>} urls - Lista de URLs para tentar
   * @param {string} method - Método HTTP (GET, POST, PUT, DELETE)
   * @param {Object} body - Dados a serem enviados na requisição
   * @param {boolean} needsAuth - Se a requisição precisa de autenticação
   * @param {string} errorMessage - Mensagem de erro padrão
   * @returns {Promise<Object>} - Resposta da API
   */
  async _requestWithFallback(urls, method = 'GET', body = null, needsAuth = true, errorMessage = 'Erro na requisição') {
    if (!Array.isArray(urls) || urls.length === 0) {
      
      return { 
        success: false, 
        error: 'URLs inválidas para requisição' 
      };
    }
    
    let lastError = null;
    
    for (let i = 0; i < urls.length; i++) {
      try {
        const url = urls[i];
        
        
        // Configurar headers
        const headers = {
          'Content-Type': 'application/json'
        };
        
        if (needsAuth && this.token) {
          headers['Authorization'] = `Bearer ${this.token}`;
        }
        
        // Realizar a requisição
        const response = await fetch(url, {
          method,
          headers,
          body: body ? JSON.stringify(body) : null,
          mode: 'cors',
          credentials: 'omit'
        });
        
        
        
        try {
          // Tentar ler o corpo da resposta
          const responseData = await response.json();
          
          
          if (response.ok) {
            
            
            // Verificar se a resposta já está no formato esperado
            if (responseData && typeof responseData === 'object') {
              if (responseData.success !== undefined) {
                // Resposta já tem a estrutura esperada {success, data, error}
                return responseData;
              } else {
                // Resposta é um objeto mas sem a estrutura esperada, vamos formatá-la
                return {
                  success: true,
                  data: responseData
                };
              }
            } else {
              // Resposta não é um objeto, vamos formatá-la
              return {
                success: true,
                data: responseData
              };
            }
          } else {
            // Resposta não foi ok (status 2xx)
            
            lastError = {
              status: response.status,
              message: responseData.error || responseData.message || `Erro ${response.status}`,
              data: responseData
            };
          }
        } catch (parseError) {
          // Erro ao processar o JSON da resposta
          
          
          // Se a resposta foi OK apesar do erro de parsing, podemos ter um problema de formato
          if (response.ok) {
            // Tentar obter o texto da resposta
            try {
              const textResponse = await response.text();
              
              
              if (textResponse.trim()) {
                // Se temos algum texto, retornar como dados
                return {
                  success: true,
                  data: textResponse,
                  warning: 'Resposta não é um JSON válido'
                };
              }
            } catch (textError) {
              
            }
            
            // Se não conseguimos ler texto ou o texto estava vazio, retornar sucesso sem dados
            return {
              success: true,
              data: {},
              warning: 'Resposta vazia ou formato inválido'
            };
          }
          
          lastError = {
            status: response.status,
            message: 'Formato de resposta inválido',
            originalError: parseError
          };
        }
      } catch (networkError) {
        // Erro de rede ao fazer a requisição
        
        lastError = {
          message: 'Erro de conexão',
          originalError: networkError
        };
      }
    }
    
    // Se chegamos aqui, todas as URLs falharam
    
    return {
      success: false,
      error: errorMessage,
      details: lastError ? lastError.message : 'Erro desconhecido',
      originalError: lastError
    };
  }

  // Método para obter obras para exibição na Home
  async getObras() {
    try {
      
      
      // Usar a mesma lógica do getDiarioObras
      const response = await this.getDiarioObras();
      
      if (response.success) {
        // Verificar se temos a lista de obras na resposta e formatá-la adequadamente
        const obras = response.data?.obras || [];
        
        // Processar os dados das obras para garantir formato consistente para a Home
        const obrasProcessadas = obras.map(obra => ({
          id: obra.id,
          nome: obra.nome || `Obra ${obra.id}`,
          cliente: obra.cliente,
          responsavel: obra.responsavel || obra.engenheiroResponsavel,
          endereco: obra.endereco,
          status: obra.status || 'Em andamento',
          dataInicio: obra.dataInicio,
          previsaoTermino: obra.dataPrevisaoFim,
          progresso: obra.progresso || 0,
          createdAt: obra.createdAt || obra.dataCriacao || new Date().toISOString(),
          updatedAt: obra.updatedAt || obra.dataAtualizacao || new Date().toISOString(),
          imagem: obra.imagem // URL da imagem, se houver
        }));
        
        
        return { success: true, data: obrasProcessadas };
      }
      
      // Se a chamada falhou, retornar o erro original
      return response;
    } catch (error) {
      
      return { 
        success: false, 
        error: 'Erro ao carregar obras',
        details: error.message
      };
    }
  }

  // Método para obter lista de clientes
  async getClientes() {
    try {
      

      // Primeiro, tentar usar o cache local se disponível
      const cachedClientes = localStorage.getItem('cached_clientes');
      let clientesFromCache = [];
      
      if (cachedClientes) {
        try {
          clientesFromCache = JSON.parse(cachedClientes);
          
        } catch (error) {
          
          // Invalidar cache corrompido
          localStorage.removeItem('cached_clientes');
        }
      }
      
      // URLs a tentar, em ordem de prioridade
      const urls = [
        API_URLS.getClientesUrl(),    // URL direta primária
        API_URLS.clientes_direct,     // URL direta alternativa
        API_URLS.clientes_alt,        // URL alternativa (Cloud Functions)
        `${API_URLS.api_direct}clientes` // URL relativa à base da API
      ];
      
      // Tentar obter clientes da API com timeout para não bloquear UI
      const timeout = 5000; // 5 segundos
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      try {
        // Tentar obter clientes da API com o método _requestWithFallback
        const response = await this._requestWithFallback(
          urls,
          'GET',
          null,
          true,
          'Erro ao buscar clientes'
        );
        
        clearTimeout(timeoutId); // Limpar timeout
        
        if (response.success && response.data) {
          
          
          // Extrair os clientes da resposta - pode estar em diferentes formatos
          let clientesFromApi = [];
          
          // Caso 1: A resposta é um array diretamente
          if (Array.isArray(response.data)) {
            clientesFromApi = response.data;
            
          }
          // Caso 2: A resposta tem um campo "clientes"
          else if (response.data.clientes && Array.isArray(response.data.clientes)) {
            clientesFromApi = response.data.clientes;
            
          }
          // Caso 3: Procurar por qualquer array na resposta que possa conter clientes
          else {
            const keys = Object.keys(response.data);
            for (const key of keys) {
              if (Array.isArray(response.data[key])) {
                const possiveisClientes = response.data[key].filter(item => 
                  item && typeof item === 'object' && (item.nome || item.id)
                );
                
                if (possiveisClientes.length > 0) {
                  clientesFromApi = possiveisClientes;
                  
                  break;
                }
              }
            }
          }
          
          // Se encontramos clientes na API, atualizar o cache e retornar
          if (clientesFromApi.length > 0) {
            // Garantir que todos os itens têm um ID
            clientesFromApi = clientesFromApi.map(cliente => ({
              ...cliente,
              id: cliente.id || this.gerarIdCliente(cliente.nome || 'cliente')
            }));
            
            // Atualizar cache
            localStorage.setItem('cached_clientes', JSON.stringify(clientesFromApi));
            
            
            return {
              success: true,
              data: { clientes: clientesFromApi }
            };
          }
          
          // Se a API não retornou clientes, verificar caso específico
          if (response.data && typeof response.data === 'object' && Object.keys(response.data).length === 0) {
            // A API retornou um objeto vazio - provavelmente não há clientes
            
            
            // Se temos clientes no cache, usá-los mesmo assim
            if (clientesFromCache.length > 0) {
              
              return {
                success: true,
                data: { clientes: clientesFromCache },
                warning: 'API retornou vazio, usando dados do cache'
              };
            }
            
            // Sem clientes na API nem no cache
            return {
              success: true,
              data: { clientes: [] }
            };
          }
        }
        
        // Se chegamos aqui, a API falhou ou retornou formato não reconhecido
        
        
        // Usar cache como fallback se disponível
        if (clientesFromCache.length > 0) {
          
          return {
            success: true,
            data: { clientes: clientesFromCache },
            warning: 'Usando dados locais devido a erro ou resposta inválida da API'
          };
        }
      } catch (fetchError) {
        clearTimeout(timeoutId); // Limpar timeout
        
        
        // Usar cache como fallback
        if (clientesFromCache.length > 0) {
          return {
            success: true,
            data: { clientes: clientesFromCache },
            warning: 'Usando dados locais devido a erro na comunicação com a API'
          };
        }
      }
      
      // Se chegamos aqui, não conseguimos obter clientes da API nem do cache
      
      return { 
        success: true, 
        data: { clientes: [] },
        warning: 'Nenhum cliente encontrado'
      };
    } catch (error) {
      
      return { 
        success: false, 
        error: 'Erro ao carregar clientes',
        details: error.message
      };
    }
  }
  
  // Método para cadastrar um novo cliente
  async cadastrarCliente(clienteData) {
    try {
      
      
      // Adicionar dados necessários
      const dadosCliente = {
        ...clienteData,
        id: this.gerarIdCliente(clienteData.nome),
        empresa: this.user?.empresa || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // URLs a tentar, em ordem de prioridade
      const urls = [
        API_URLS.getClientesUrl(),    // URL direta primária
        API_URLS.clientes_direct,     // URL direta alternativa
        API_URLS.clientes_alt,        // URL alternativa (Cloud Functions)
        `${API_URLS.api_direct}clientes` // URL relativa à base da API
      ];
      
      // Enviar para a API
      const response = await this._requestWithFallback(
        urls,
        'POST',
        dadosCliente,
        true,
        'Erro ao cadastrar cliente'
      );
      
      if (response.success) {
        
        
        // Atualizar cache local
        this.atualizarCacheClientes(dadosCliente);
        
        return response;
      }
      
      // Se a API falhar, salvar localmente e retornar sucesso
      
      this.atualizarCacheClientes(dadosCliente);
      
      return { 
        success: true, 
        data: { cliente: dadosCliente },
        warning: 'Cliente salvo apenas localmente devido a falha na API'
      };
    } catch (error) {
      
      return { 
        success: false, 
        error: 'Erro ao cadastrar cliente',
        details: error.message
      };
    }
  }
  
  // Método para atualizar um cliente existente
  async atualizarCliente(clienteId, clienteData) {
    try {
      
      
      // Preparar dados para atualização
      const dadosCliente = {
        ...clienteData,
        updatedAt: new Date().toISOString()
      };
      
      // URLs a tentar, em ordem de prioridade
      const urls = [
        API_URLS.updateClienteUrl(clienteId),   // URL direta para atualização
        `${API_URLS.clientes_direct}/${clienteId}`, // URL alternativa
        `${API_URLS.clientes_alt}/${clienteId}`, // URL via Cloud Functions
        `${API_URLS.api_direct}clientes/${clienteId}` // URL relativa à base da API
      ];
      
      // Enviar para a API
      const response = await this._requestWithFallback(
        urls,
        'PUT',
        dadosCliente,
        true,
        'Erro ao atualizar cliente'
      );
      
      if (response.success) {
        
        
        // Atualizar cache local
        this.atualizarCacheClientes({
          ...dadosCliente,
          id: clienteId
        });
        
        return response;
      }
      
      // Se a API falhar, atualizar localmente e retornar sucesso
      
      this.atualizarCacheClientes({
        ...dadosCliente,
        id: clienteId
      });
      
      return { 
        success: true, 
        data: { cliente: dadosCliente },
        warning: 'Cliente atualizado apenas localmente devido a falha na API'
      };
    } catch (error) {
      
      return { 
        success: false, 
        error: 'Erro ao atualizar cliente',
        details: error.message
      };
    }
  }
  
  // Método para excluir um cliente
  async excluirCliente(clienteId) {
    try {
      
      
      // URLs a tentar, em ordem de prioridade
      const urls = [
        API_URLS.deleteClienteUrl(clienteId),   // URL direta para exclusão
        `${API_URLS.clientes_direct}/${clienteId}`, // URL alternativa
        `${API_URLS.clientes_alt}/${clienteId}`, // URL via Cloud Functions
        `${API_URLS.api_direct}clientes/${clienteId}` // URL relativa à base da API
      ];
      
      // Enviar para a API
      const response = await this._requestWithFallback(
        urls,
        'DELETE',
        null,
        true,
        'Erro ao excluir cliente'
      );
      
      if (response.success) {
        
        
        // Atualizar cache local removendo o cliente
        this.removerClienteDoCache(clienteId);
        
        return response;
      }
      
      // Se a API falhar, remover localmente e retornar sucesso
      
      this.removerClienteDoCache(clienteId);
      
      return { 
        success: true, 
        warning: 'Cliente removido apenas localmente devido a falha na API'
      };
    } catch (error) {
      
      return { 
        success: false, 
        error: 'Erro ao excluir cliente',
        details: error.message
      };
    }
  }
  
  // Método para obter detalhes de um cliente específico
  async getClienteDetalhes(clienteId) {
    try {
      
      
      // URLs a tentar, em ordem de prioridade
      const urls = [
        API_URLS.getClienteUrl(clienteId),     // URL direta
        `${API_URLS.clientes_direct}/${clienteId}`, // URL alternativa
        `${API_URLS.clientes_alt}/${clienteId}`, // URL via Cloud Functions
        `${API_URLS.api_direct}clientes/${clienteId}` // URL relativa à base da API
      ];
      
      // Tentar obter cliente da API
      const response = await this._requestWithFallback(
        urls,
        'GET',
        null,
        true,
        'Erro ao buscar detalhes do cliente'
      );
      
      if (response.success) {
        
        return response;
      }
      
      // Se a API falhar, tentar usar dados do localStorage como fallback
      const cachedClientesStr = localStorage.getItem('cached_clientes');
      if (cachedClientesStr) {
        const clientes = JSON.parse(cachedClientesStr);
        const cliente = clientes.find(c => c.id === clienteId);
        
        if (cliente) {
          
          return { success: true, data: { cliente } };
        }
      }
      
      // Se não encontrou, retornar erro
      return { 
        success: false, 
        error: 'Cliente não encontrado'
      };
    } catch (error) {
      
      return { 
        success: false, 
        error: 'Erro ao carregar detalhes do cliente',
        details: error.message
      };
    }
  }

  // Método auxiliar para gerar ID do cliente
  gerarIdCliente(nome) {
    // Normalizar o nome do cliente
    const normalizeText = (text) => {
      if (!text) return '';
      
      // Converter para minúsculas e remover espaços no início e fim
      let normalized = text.trim().toLowerCase();
      
      // Remover acentos
      normalized = normalized.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      
      // Substituir espaços e caracteres especiais por underscore
      normalized = normalized.replace(/[^a-z0-9]/g, '_');
      
      // Remover underscores múltiplos
      normalized = normalized.replace(/_+/g, '_');
      
      // Remover underscore no início e no fim
      normalized = normalized.replace(/^_|_$/g, '');
      
      return normalized;
    };
    
    const nomeNormalizado = normalizeText(nome);
    const empresa = normalizeText(this.user?.empresa || '');
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.random().toString(36).substring(2, 6);
    
    return `${empresa}_${nomeNormalizado}_${timestamp}${random}`;
  }
  
  // Método auxiliar para atualizar o cache de clientes
  atualizarCacheClientes(novoCliente) {
    try {
      // Obter clientes do cache
      const cachedClientesStr = localStorage.getItem('cached_clientes');
      let clientes = [];
      
      if (cachedClientesStr) {
        clientes = JSON.parse(cachedClientesStr);
      }
      
      // Verificar se o cliente já existe
      const clienteExistente = clientes.findIndex(c => c.id === novoCliente.id);
      
      if (clienteExistente >= 0) {
        // Atualizar cliente existente
        clientes[clienteExistente] = { ...clientes[clienteExistente], ...novoCliente };
      } else {
        // Adicionar novo cliente
        clientes.push(novoCliente);
      }
      
      // Salvar no localStorage
      localStorage.setItem('cached_clientes', JSON.stringify(clientes));
      
    } catch (error) {
      
    }
  }
  
  // Método auxiliar para remover cliente do cache
  removerClienteDoCache(clienteId) {
    try {
      // Obter clientes do cache
      const cachedClientesStr = localStorage.getItem('cached_clientes');
      
      if (!cachedClientesStr) return;
      
      let clientes = JSON.parse(cachedClientesStr);
      
      // Filtrar o cliente a ser removido
      clientes = clientes.filter(c => c.id !== clienteId);
      
      // Salvar no localStorage
      localStorage.setItem('cached_clientes', JSON.stringify(clientes));
      
    } catch (error) {
      
    }
  }

  // Método para atualizar dados da obra no diário de obra
  async updateDiarioObra(obraId, dadosObra) {
    if (!obraId || !dadosObra) {
      
      return { 
        success: false, 
        error: 'ID da obra e dados são obrigatórios' 
      };
    }

    try {
      
      
      // Verificar se o ID da obra é válido
      if (!this.isValidObraId(obraId)) {
        
      }
      
      // Usar a URL do endpoint direto conforme definido em apiConfig
      const url = API_URLS.updateDiarioObraUrl(obraId);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...dadosObra,
          id: obraId,
          timestamp: new Date().toISOString()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        
        throw new Error(`Erro ao atualizar obra: ${errorData.error || response.status}`);
      }
      
      const data = await response.json();
      
      
      return {
        success: true,
        data: data.data || {
          mensagem: 'Obra atualizada com sucesso',
          obra: {
            ...dadosObra,
            id: obraId,
            dataAtualizacao: new Date().toISOString()
          }
        }
      };
    } catch (error) {
      
      return {
        success: false,
        error: 'Erro ao atualizar dados da obra',
        details: error.message
      };
    }
  }

  async alterarSenha(data) {
    try {
      // Tentar diferentes URLs em ordem de prioridade
      const urls = [
        API_URLS.alterarSenha_direct,    // URL direta do Cloud Run (prioridade)
        API_URLS.alterarSenha_alt,       // URL alternativa do Cloud Functions
        API_URLS.alterarSenha            // URL principal
      ];

      let lastError = null;
      for (const url of urls) {
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.token}`
            },
            body: JSON.stringify(data)
          });

          const result = await response.json();

          if (!response.ok) {
            throw new Error(result.error || 'Erro ao alterar senha');
          }

          return {
            success: true,
            message: result.message
          };
        } catch (error) {
          
          lastError = error;
          continue;
        }
      }

      // Se chegou aqui, todas as tentativas falharam
      
      return {
        success: false,
        error: lastError.message
      };
    } catch (error) {
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Método para cadastrar cliente
  async cadastrarCliente(dados) {
    try {
      
      
      // Tentar diferentes URLs para o endpoint
      const urls = [
        `${API_URLS.api_direct}clientes`,           // URL direta
        `${API_URLS.api}clientes`,                  // URL via Firebase Hosting
        `${API_URLS.clientes_direct}`               // URL alternativa
      ];
      
      let lastError = null;
      
      for (const url of urls) {
        try {
          
          
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.token}`
            },
            body: JSON.stringify(dados)
          });
          
          const data = await response.json();
          
          if (response.ok) {
            
            
            // Atualizar cache de clientes
            if (data.success && data.data) {
              this.atualizarCacheClientes(data.data);
            }
            
            return {
              success: true,
              data: data.data || data
            };
          }
          
          lastError = data.error || `Erro ao cadastrar cliente: ${response.status}`;
          
          
          // Se não for erro 404, não tentar outras URLs
          if (response.status !== 404) {
            break;
          }
        } catch (error) {
          
          lastError = error.message;
          // Continuar para a próxima URL
        }
      }
      
      // Se chegou aqui, todas as tentativas falharam
      return {
        success: false,
        error: lastError || 'Não foi possível cadastrar o cliente. Todos os endpoints falharam.'
      };
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Erro ao cadastrar cliente'
      };
    }
  }
  /**
   * Compartilha uma obra com outro usuário
   * @param {string} obraId - ID da obra a ser compartilhada
   * @param {string} emailDestinatario - Email do destinatário
   * @param {Array<string>} permissoes - Lista de permissões (opcional)
   * @returns {Promise<Object>} Resultado da operação
   */
  async compartilharObra(obraId, emailDestinatario, permissoes = []) {
    try {
      
      
      // Garantir que o ID da obra está no formato correto
      const obraIdNormalizado = obraId.toLowerCase().replace(/\s+/g, '_');
      
      
      const url = API_URLS.compartilharObra;
      
      
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getToken()}`
      };
      
      
      const body = JSON.stringify({
        obraId: obraIdNormalizado,
        emailDestinatario: emailDestinatario.toLowerCase(),
        permissoes
      });
      
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body,
        mode: 'cors'
      });
  
      
      
      const responseData = await response.json();
      
      if (!response.ok) {
        
        return { 
          success: false, 
          error: responseData.error,
          message: responseData.message || responseData.error
        };
      }
  
      return { success: true, data: responseData };
    } catch (error) {
      
      return { 
        success: false, 
        error: error.message || 'Erro ao compartilhar obra',
        message: error.message || 'Erro ao compartilhar obra'
      };
    }
  }

  /**
   * Aceita um convite de compartilhamento de obra
   * @param {string} codigoCompartilhamento - Código de compartilhamento recebido
   * @returns {Promise<Object>} Resultado da operação
   */
  async aceitarCompartilhamento(codigoCompartilhamento) {
    try {
      
      
      const url = API_URLS.getAceitarCompartilhamentoUrl();
      
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`
        },
        body: JSON.stringify({
          codigoCompartilhamento
        })
      });
      
      if (!response.ok) {
        
        const errorData = await response.json().catch(e => ({ error: 'Erro ao processar resposta' }));
        return { success: false, error: errorData.error || `Status ${response.status}` };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      
      return { success: false, error: 'Erro de conexão com o servidor' };
    }
  }

  /**
   * Lista todas as obras compartilhadas com o usuário atual
   * @returns {Promise<Object>} Lista de obras compartilhadas
   */
  async listarObrasCompartilhadas() {
    try {
      
      
      const url = API_URLS.getListarObrasCompartilhadasUrl();
      
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });
      
      if (!response.ok) {
        
        const errorData = await response.json().catch(e => ({ error: 'Erro ao processar resposta' }));
        return { success: false, error: errorData.error || `Status ${response.status}` };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      
      return { success: false, error: 'Erro de conexão com o servidor' };
    }
  }

  /**
   * Lista todas as obras pendentes de aceite para o usuário atual
   * @returns {Promise<Object>} Lista de obras pendentes
   */
  async listarObrasPendentes() {
    try {
      
      
      const url = API_URLS.getListarObrasPendentesUrl();
      
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getToken()}`
        }
      });
      
      if (!response.ok) {
        
        const errorData = await response.json().catch(e => ({ error: 'Erro ao processar resposta' }));
        return { success: false, error: errorData.error || `Status ${response.status}` };
      }
      
      const data = await response.json();
      
      return data;
    } catch (error) {
      
      return { success: false, error: 'Erro de conexão com o servidor' };
    }
  }

  /**
   * Aceita o compartilhamento de uma obra
   * @param {string} obraId - ID da obra
   * @param {string} codigoCompartilhamento - Código de compartilhamento
   * @returns {Promise<Object>} Resultado da operação
   */
  async aceitarCompartilhamentoObra(obraId, codigoCompartilhamento) {
    try {
      
      
      const url = API_URLS.getAceitarCompartilhamentoUrl();
      
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getToken()}`
        },
        body: JSON.stringify({
          obraId,
          codigoCompartilhamento
        })
      });
      
      if (!response.ok) {
        
        const errorData = await response.json().catch(e => ({ error: 'Erro ao processar resposta' }));
        return { success: false, error: errorData.error || `Status ${response.status}` };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      
      return { success: false, error: 'Erro de conexão com o servidor' };
    }
  }

  /**
   * Rejeita o compartilhamento de uma obra
   * @param {string} obraId - ID da obra
   * @param {string} codigoCompartilhamento - Código de compartilhamento
   * @returns {Promise<Object>} Resultado da operação
   */
  async rejeitarCompartilhamentoObra(obraId, codigoCompartilhamento) {
    try {
      
      
      const url = API_URLS.getRejeitarCompartilhamentoUrl();
      
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getToken()}`
        },
        body: JSON.stringify({
          obraId,
          codigoCompartilhamento
        })
      });
      
      if (!response.ok) {
        
        const errorData = await response.json().catch(e => ({ error: 'Erro ao processar resposta' }));
        return { success: false, error: errorData.error || `Status ${response.status}` };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      
      return { success: false, error: 'Erro de conexão com o servidor' };
    }
  }

  // Método auxiliar para validar IDs de obra
  isValidObraId(obraId) {
    // Verifica se o ID é uma string não vazia
    if (!obraId || typeof obraId !== 'string') {
      return false;
    }

    // Verifica se o ID tem um formato válido (pode ser personalizado conforme necessário)
    const validObraIdPattern = /^[a-zA-Z0-9_-]+$/;
    return validObraIdPattern.test(obraId);
  }


    /**
     * Verifica o status de aprovação de um relatório diário específico
     * @param {string} obraId - ID da obra 
     * @param {string} data - Data no formato YYYY-MM-DD
     * @returns {Promise<Object>} - Resposta da API com o status de aprovação
     */
    async verificarAprovacaoRDO(obraId, data) {
        // Validação inicial dos parâmetros
        if (!obraId || !data) {
            
            return {
                success: false,
                error: 'ID da obra e data são obrigatórios'
            };
        }

        // Validar formato da data (YYYY-MM-DD)
        const dataRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dataRegex.test(data)) {
            
            return {
                success: false,
                error: 'Formato de data inválido. Use YYYY-MM-DD'
            };
        }

        try {
            // Formatar data para o formato do banco (YYYYMMDD)
            const dataFormatada = data.replace(/-/g, "");
            

            // Construir URL com o formato correto: /{obraId}/status?data={dataFormatada}
            const url = `${API_URLS.aprovarRDO}/${encodeURIComponent(obraId)}/status?data=${dataFormatada}`;
            

            // Fazer a requisição com os headers corretos
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${this.getToken()}`,
                    'Content-Type': 'application/json'
                }
            });

            // Log da resposta
            
            
            if (!response.ok) {
                const errorData = await response.json();
                
                return {
                    success: false,
                    error: errorData.error || 'Erro ao verificar aprovação'
                };
            }

            const responseData = await response.json();
            
            
            return {
                success: true,
                ...responseData
            };
        } catch (error) {
            
            return {
                success: false,
                error: 'Erro ao verificar aprovação do relatório diário'
            };
        }
    }

    /**
     * Lista todas as aprovações de RDO de uma obra específica
     * @param {string} obraId - ID da obra
     * @returns {Promise<Object>} - Resposta da API com a lista de aprovações
     */
    async listarAprovacoesRDO(obraId) {
        if (!obraId) {
            return {
                success: false,
                error: 'ID da obra é obrigatório'
            };
        }

        try {
            

            const urls = [
                API_URLS.getAprovarRDOListaUrl(obraId),
                API_URLS.getAprovacaoRDOListaUrl(obraId),
                `${API_URLS.aprovarRDO}/listar/${obraId}`,
                `${API_URLS.aprovarRDO_alt}/listar/${obraId}`
            ];

            return this._requestWithFallback(
                urls,
                'GET',
                null,
                true,
                'Erro ao listar aprovações de relatório diário'
            );
        } catch (error) {
            
            return {
                success: false,
                error: 'Erro ao listar aprovações',
                details: error.message
            };
        }
    }

    /**
     * Aprova um relatório diário específico
     * @param {string} obraId - ID da obra
     * @param {string} data - Data no formato YYYY-MM-DD
     * @param {string} [observacoes] - Observações sobre a aprovação
     * @returns {Promise<Object>} - Resposta da API com a confirmação
     */
    async aprovarDiaRDO(obraId, data, observacoes = '') {
        if (!obraId || !data) {
            return {
                success: false,
                error: 'ID da obra e data são obrigatórios'
            };
        }

        try {
            

            const urls = [
                API_URLS.getAprovarRDODetalheUrl(obraId, data),
                API_URLS.getAprovacaoRDODetalheUrl(obraId, data),
                `${API_URLS.aprovarRDO}/aprovar/${obraId}?data=${data}`,
                `${API_URLS.aprovarRDO_alt}/aprovar/${obraId}?data=${data}`
            ];

            return this._requestWithFallback(
                urls,
                'POST',
                { observacoes },
                true,
                'Erro ao aprovar relatório diário'
            );
        } catch (error) {
            
            return {
                success: false,
                error: 'Erro ao aprovar relatório',
                details: error.message
            };
        }
    }

    /**
     * Cancela a aprovação de um relatório diário específico
     * @param {string} obraId - ID da obra
     * @param {string} data - Data no formato YYYY-MM-DD
     * @returns {Promise<Object>} - Resposta da API com a confirmação
     */
    async cancelarAprovacaoRDO(obraId, data) {
        if (!obraId || !data) {
            return {
                success: false,
                error: 'ID da obra e data são obrigatórios'
            };
        }

        try {
            

            const urls = [
                API_URLS.getAprovarRDODetalheUrl(obraId, data),
                API_URLS.getAprovacaoRDODetalheUrl(obraId, data),
                `${API_URLS.aprovarRDO}/aprovar/${obraId}?data=${data}`,
                `${API_URLS.aprovarRDO_alt}/aprovar/${obraId}?data=${data}`
            ];

            return this._requestWithFallback(
                urls,
                'DELETE',
                null,
                true,
                'Erro ao cancelar aprovação do relatório diário'
            );
        } catch (error) {
            
            return {
                success: false,
                error: 'Erro ao cancelar aprovação',
                details: error.message
            };
        }
    }

  // Métodos para API de Controle Financeiro (Custos)
  
  /**
   * Obtém lista de todas as transações financeiras
   * @returns {Promise<Object>} Resposta da API com as transações
   */
  async getCustos() {
    try {
      const url = API_URLS.getCustosUrl();
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(e => ({ error: 'Erro desconhecido' }));
        return { success: false, error: errorData.error || 'Erro ao obter transações' };
      }
      
      const responseData = await response.json();
      
      // Normalizar a estrutura de resposta para padronizar com o frontend
      const data = this._normalizarRespostaCustos(responseData);
      
      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Erro ao carregar transações' };
    }
  }
  
  /**
   * Obtém lista de transações financeiras filtradas
   * @param {Object} filtros - Filtros a serem aplicados
   * @param {string} [filtros.tipo] - Tipo de transação ('entrada', 'saida' ou 'todos')
   * @param {string} [filtros.categoria] - Categoria das transações
   * @param {string} [filtros.dataInicio] - Data inicial (formato YYYY-MM-DD)
   * @param {string} [filtros.dataFim] - Data final (formato YYYY-MM-DD)
   * @returns {Promise<Object>} Resposta da API com as transações filtradas
   */
  async getCustosFiltrados(filtros = {}) {
    try {
      // Preparar filtros para URL
      const filtrosParams = {
        tipo: filtros.tipo !== 'todos' ? filtros.tipo : '',
        categoria: filtros.categoria !== 'todos' ? filtros.categoria : '',
        dataInicio: filtros.dataInicio || '',
        dataFim: filtros.dataFim || ''
      };
      
      // Obter URL com filtros
      const url = API_URLS.getCustosFiltradosUrl(filtrosParams);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(e => ({ error: 'Erro desconhecido' }));
        return { success: false, error: errorData.error || 'Erro ao obter transações' };
      }
      
      const responseData = await response.json();
      
      // Normalizar a estrutura de resposta para padronizar com o frontend
      const data = this._normalizarRespostaCustos(responseData);
      
      return { success: true, data };
    } catch (error) {
      return { 
        success: false, 
        error: 'Erro ao carregar transações',
        details: error.message
      };
    }
  }
  
  /**
   * Método privado para normalizar a estrutura da resposta da API de custos
   * @param {Object} responseData - Dados da resposta da API
   * @returns {Object} Dados normalizados
   */
  _normalizarRespostaCustos(responseData) {
    // Verificar se a resposta já tem a estrutura esperada
    if (responseData.transacoes) {
      return responseData;
    }
    
    // Caso 1: A resposta é diretamente um array de transações
    if (Array.isArray(responseData)) {
      return {
        transacoes: responseData,
        saldo: this._calcularSaldo(responseData)
      };
    }
    
    // Caso 2: Os dados estão dentro de um objeto 'data'
    if (responseData.data) {
      if (Array.isArray(responseData.data)) {
        return {
          transacoes: responseData.data,
          saldo: this._calcularSaldo(responseData.data)
        };
      }
      
      if (responseData.data.transacoes) {
        return responseData.data;
      }
    }
    
    // Caso 3: Estrutura desconhecida, tentar extrair os dados
    const possiveisCampos = Object.keys(responseData).filter(key => 
      Array.isArray(responseData[key]) && 
      responseData[key].length > 0 &&
      responseData[key][0] && 
      (responseData[key][0].tipo === 'entrada' || responseData[key][0].tipo === 'saida')
    );
    
    if (possiveisCampos.length > 0) {
      const transacoes = responseData[possiveisCampos[0]];
      return {
        transacoes,
        saldo: typeof responseData.saldo !== 'undefined' ? responseData.saldo : this._calcularSaldo(transacoes)
      };
    }
    
    // Caso 4: Não foi possível extrair os dados, retornar objeto vazio
    return {
      transacoes: [],
      saldo: 0
    };
  }
  
  /**
   * Método privado para calcular o saldo com base nas transações
   * @param {Array} transacoes - Lista de transações
   * @returns {number} Saldo calculado
   */
  _calcularSaldo(transacoes) {
    if (!Array.isArray(transacoes) || transacoes.length === 0) {
      return 0;
    }
    
    return transacoes.reduce((saldo, transacao) => {
      const valor = parseFloat(transacao.valor) || 0;
      return transacao.tipo === 'entrada' ? saldo + valor : saldo - valor;
    }, 0);
  }
  
  /**
   * Cria uma nova transação financeira
   * @param {Object} transacaoData - Dados da transação
   * @param {string} transacaoData.tipo - Tipo da transação ('entrada' ou 'saida')
   * @param {string} transacaoData.descricao - Descrição da transação
   * @param {number|string} transacaoData.valor - Valor da transação
   * @param {string} transacaoData.categoria - Categoria da transação
   * @param {string} transacaoData.data - Data da transação (formato YYYY-MM-DD)
   * @param {string} transacaoData.formaPagamento - Forma de pagamento
   * @param {string} transacaoData.entidadeNome - Nome da entidade relacionada (cliente, funcionário, etc)
   * @param {string} transacaoData.entidadeTipo - Tipo da entidade (cliente, funcionario, outros)
   * @param {boolean} transacaoData.reembolsadoPeloCliente - Indica se a despesa foi reembolsada pelo cliente
   * @returns {Promise<Object>} Resposta da API com a transação criada
   */
  async createCusto(transacaoData) {
    try {
      const url = API_URLS.createCustoUrl();
      
      // Validar dados
      if (!transacaoData.descricao || !transacaoData.valor || parseFloat(transacaoData.valor) <= 0) {
        return { success: false, error: "Preencha todos os campos corretamente" };
      }
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transacaoData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(e => ({ error: 'Erro desconhecido' }));
        return { success: false, error: errorData.error || 'Erro ao criar transação' };
      }
      
      const responseData = await response.json();
      
      // Normalizar a estrutura de resposta
      const data = this._normalizarRespostaCustos(responseData);
      
      return { success: true, data };
    } catch (error) {
      return { 
        success: false, 
        error: 'Erro ao salvar transação',
        details: error.message
      };
    }
  }
  
  /**
   * Atualiza uma transação financeira existente
   * @param {string} id - ID da transação a ser atualizada
   * @param {Object} transacaoData - Novos dados da transação
   * @returns {Promise<Object>} Resposta da API com a transação atualizada
   */
  async updateCusto(id, transacaoData) {
    try {
      if (!id) {
        return { success: false, error: 'ID da transação é obrigatório' };
      }
      
      const url = API_URLS.updateCustoUrl(id);
      
      // Validar dados
      if (!transacaoData.descricao || !transacaoData.valor || parseFloat(transacaoData.valor) <= 0) {
        return { success: false, error: "Preencha todos os campos corretamente" };
      }
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transacaoData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(e => ({ error: 'Erro desconhecido' }));
        return { success: false, error: errorData.error || 'Erro ao atualizar transação' };
      }
      
      const responseData = await response.json();
      
      // Normalizar a estrutura de resposta
      const data = this._normalizarRespostaCustos(responseData);
      
      return { success: true, data };
    } catch (error) {
      return { 
        success: false, 
        error: 'Erro ao atualizar transação',
        details: error.message
      };
    }
  }
  
  /**
   * Exclui uma transação financeira
   * @param {string} id - ID da transação a ser excluída
   * @returns {Promise<Object>} Resposta da API confirmando a exclusão
   */
  async deleteCusto(id) {
    try {
      if (!id) {
        return { success: false, error: 'ID da transação é obrigatório' };
      }
      
      const url = API_URLS.deleteCustoUrl(id);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(e => ({ error: 'Erro desconhecido' }));
        return { success: false, error: errorData.error || 'Erro ao excluir transação' };
      }
      
      const responseData = await response.json();
      
      return { 
        success: true, 
        data: responseData
      };
    } catch (error) {
      return { 
        success: false, 
        error: 'Erro ao excluir transação',
        details: error.message
      };
    }
  }
  
  /**
   * Obtém um resumo financeiro com saldos e totais
   * @param {Object} [filtros] - Filtros para o período desejado
   * @returns {Promise<Object>} Resposta da API com o resumo financeiro
   */
  async getResumoFinanceiro(filtros = {}) {
    try {
      // Preparar filtros para URL
      const filtrosParams = {
        dataInicio: filtros.dataInicio || '',
        dataFim: filtros.dataFim || ''
      };
      
      // Obter URL com filtros
      const url = API_URLS.getResumoFinanceiroUrl(filtrosParams);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(e => ({ error: 'Erro desconhecido' }));
        return { success: false, error: errorData.error || 'Erro ao obter resumo financeiro' };
      }
      
      const responseData = await response.json();
      
      // Normalizar a estrutura de resposta
      let data = responseData;
      
      // Se a resposta tem um campo data, usar esse campo
      if (responseData.data) {
        data = responseData.data;
      }
      
      return { success: true, data };
    } catch (error) {
      return { 
        success: false, 
        error: 'Erro ao carregar resumo financeiro',
        details: error.message
      };
    }
  }

  // Métodos para recuperação de senha
  async solicitarRecuperacaoSenha(email) {
    try {
      const urls = [
        API_URLS.solicitarRecuperacaoSenha,
        API_URLS.solicitarRecuperacaoSenha_direct,
        API_URLS.solicitarRecuperacaoSenha_alt
      ];

      const response = await this._requestWithFallback(
        urls,
        'POST',
        { email },
        false,
        'Erro ao solicitar recuperação de senha'
      );

      if (response.success) {
        return {
          success: true,
          message: 'Email de recuperação enviado com sucesso'
        };
      }

      return {
        success: false,
        error: response.error || 'Erro ao solicitar recuperação de senha'
      };
    } catch (error) {
      return {
        success: false,
        error: 'Erro ao processar solicitação de recuperação de senha',
        details: error.message
      };
    }
  }

  async redefinirSenha(token, novaSenha) {
    try {
      const urls = [
        API_URLS.redefinirSenha,
        API_URLS.redefinirSenha_direct,
        API_URLS.redefinirSenha_alt
      ];

      const response = await this._requestWithFallback(
        urls,
        'POST',
        { token, novaSenha },
        false,
        'Erro ao redefinir senha'
      );

      if (response.success) {
        return {
          success: true,
          message: 'Senha redefinida com sucesso'
        };
      }

      return {
        success: false,
        error: response.error || 'Erro ao redefinir senha'
      };
    } catch (error) {
      return {
        success: false,
        error: 'Erro ao processar redefinição de senha',
        details: error.message
      };
    }
  }

  // Métodos para verificação de email
  async solicitarVerificacaoEmail(email, nome) {
    try {
      const url = this.useDirectUrls 
        ? API_URLS.getSolicitarVerificacaoEmailUrl()
        : API_URLS.getSolicitarVerificacaoEmailAltUrl();

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, nome })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(e => ({ error: 'Erro ao processar resposta' }));
        return { success: false, error: errorData.error || `Status ${response.status}` };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Erro de conexão com o servidor' };
    }
  }

  async verificarEmail(token) {
    try {
      const url = this.useDirectUrls 
        ? API_URLS.getVerificarEmailUrl()
        : API_URLS.getVerificarEmailAltUrl();

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(e => ({ error: 'Erro ao processar resposta' }));
        return { success: false, error: errorData.error || `Status ${response.status}` };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Erro de conexão com o servidor' };
    }
  }

  /**
   * Exclui uma obra do diário de obras
   * @param {string} obraId - ID da obra a ser excluída
   * @returns {Promise<Object>} - Resultado da operação
   */
  async deleteDiarioObra(obraId) {
    if (!obraId) {
      return { success: false, error: 'ID da obra é obrigatório' };
    }

    const urls = [
      `${API_URLS.diarioObras}/${obraId}`,
      `${API_URLS.diarioObras_direct}/${obraId}`,
      `${API_URLS.api_direct}diarioObras/${obraId}`,
      `${API_URLS.api}diarioObras/${obraId}`
    ];

    let lastError = null;

    for (const url of urls) {
      try {
        const response = await fetch(url, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json().catch(() => ({ 
          error: `Erro ao processar resposta do servidor (${response.status})` 
        }));

        if (response.ok) {
          return { 
            success: true, 
            data: data || { message: 'Obra excluída permanentemente com sucesso' } 
          };
        }

        lastError = {
          status: response.status,
          url,
          error: data.error || `Erro ${response.status}`
        };

      } catch (error) {
        lastError = {
          url,
          error: error.message,
          type: error.name
        };
      }
    }

    return { 
      success: false, 
      error: 'Não foi possível excluir a obra. Tente novamente mais tarde.',
      details: lastError
    };
  }

  // Métodos para Controle de Diárias de Pedreiros
  async getPedreiros() {
    try {
      // URLs a tentar, em ordem de prioridade
      const urls = [
        API_URLS.getPedreirosUrl(),    // URL direta primária
        API_URLS.pedreiros_direct,     // URL direta alternativa
        API_URLS.pedreiros_alt,        // URL alternativa (Cloud Functions)
        `${API_URLS.api_direct}pedreiros` // URL relativa à base da API
      ];
      
      return this._requestWithFallback(
        urls,
        'GET',
        null,
        true,
        'Erro ao buscar pedreiros'
      );
    } catch (error) {
      console.error('Erro ao buscar pedreiros:', error);
      return { success: false, message: 'Erro ao buscar pedreiros' };
    }
  }

  async addPedreiro(pedreiroData) {
    try {
      // URLs a tentar, em ordem de prioridade
      const urls = [
        API_URLS.getPedreirosUrl(),    // URL direta primária
        API_URLS.pedreiros_direct,     // URL direta alternativa
        API_URLS.pedreiros_alt,        // URL alternativa (Cloud Functions)
        `${API_URLS.api_direct}pedreiros` // URL relativa à base da API
      ];
      
      return this._requestWithFallback(
        urls,
        'POST',
        pedreiroData,
        true,
        'Erro ao adicionar pedreiro'
      );
    } catch (error) {
      console.error('Erro ao adicionar pedreiro:', error);
      return { success: false, message: 'Erro ao adicionar pedreiro' };
    }
  }

  async updatePedreiro(pedreiroId, pedreiroData) {
    try {
      // URLs a tentar, em ordem de prioridade
      const urls = [
        API_URLS.updatePedreiroUrl(pedreiroId),    // URL direta primária
        `${API_URLS.pedreiros_direct}/${pedreiroId}`,     // URL direta alternativa
        `${API_URLS.pedreiros_alt}/${pedreiroId}`,        // URL alternativa (Cloud Functions)
        `${API_URLS.api_direct}pedreiros/${pedreiroId}` // URL relativa à base da API
      ];
      
      return this._requestWithFallback(
        urls,
        'PUT',
        pedreiroData,
        true,
        'Erro ao atualizar pedreiro'
      );
    } catch (error) {
      console.error('Erro ao atualizar pedreiro:', error);
      return { success: false, message: 'Erro ao atualizar pedreiro' };
    }
  }

  async deletePedreiro(pedreiroId) {
    try {
      // URLs a tentar, em ordem de prioridade
      const urls = [
        API_URLS.deletePedreiroUrl(pedreiroId),    // URL direta primária
        `${API_URLS.pedreiros_direct}/${pedreiroId}`,     // URL direta alternativa
        `${API_URLS.pedreiros_alt}/${pedreiroId}`,        // URL alternativa (Cloud Functions)
        `${API_URLS.api_direct}pedreiros/${pedreiroId}` // URL relativa à base da API
      ];
      
      return this._requestWithFallback(
        urls,
        'DELETE',
        null,
        true,
        'Erro ao excluir pedreiro'
      );
    } catch (error) {
      console.error('Erro ao excluir pedreiro:', error);
      return { success: false, message: 'Erro ao excluir pedreiro' };
    }
  }

  async getDiarias() {
    try {
      // URLs a tentar, em ordem de prioridade
      const urls = [
        API_URLS.getDiariasUrl(),    // URL direta primária
        API_URLS.diarias_direct,     // URL direta alternativa
        API_URLS.diarias_alt,        // URL alternativa (Cloud Functions)
        `${API_URLS.api_direct}diarias` // URL relativa à base da API
      ];
      
      return this._requestWithFallback(
        urls,
        'GET',
        null,
        true,
        'Erro ao buscar diárias'
      );
    } catch (error) {
      console.error('Erro ao buscar diárias:', error);
      return { success: false, message: 'Erro ao buscar diárias' };
    }
  }

  async addDiaria(diariaData) {
    try {
      // URLs a tentar, em ordem de prioridade
      const urls = [
        API_URLS.getDiariasUrl(),    // URL direta primária
        API_URLS.diarias_direct,     // URL direta alternativa
        API_URLS.diarias_alt,        // URL alternativa (Cloud Functions)
        `${API_URLS.api_direct}diarias` // URL relativa à base da API
      ];
      
      return this._requestWithFallback(
        urls,
        'POST',
        diariaData,
        true,
        'Erro ao adicionar diária'
      );
    } catch (error) {
      console.error('Erro ao adicionar diária:', error);
      return { success: false, message: 'Erro ao adicionar diária' };
    }
  }

  async updateDiaria(diariaId, diariaData) {
    try {
      // URLs a tentar, em ordem de prioridade
      const urls = [
        API_URLS.updateDiariaUrl(diariaId),    // URL direta primária
        `${API_URLS.diarias_direct}/${diariaId}`,     // URL direta alternativa
        `${API_URLS.diarias_alt}/${diariaId}`,        // URL alternativa (Cloud Functions)
        `${API_URLS.api_direct}diarias/${diariaId}` // URL relativa à base da API
      ];
      
      return this._requestWithFallback(
        urls,
        'PUT',
        diariaData,
        true,
        'Erro ao atualizar diária'
      );
    } catch (error) {
      console.error('Erro ao atualizar diária:', error);
      return { success: false, message: 'Erro ao atualizar diária' };
    }
  }

  async deleteDiaria(diariaId) {
    try {
      // URLs a tentar, em ordem de prioridade
      const urls = [
        API_URLS.deleteDiariaUrl(diariaId),    // URL direta primária
        `${API_URLS.diarias_direct}/${diariaId}`,     // URL direta alternativa
        `${API_URLS.diarias_alt}/${diariaId}`,        // URL alternativa (Cloud Functions)
        `${API_URLS.api_direct}diarias/${diariaId}` // URL relativa à base da API
      ];
      
      return this._requestWithFallback(
        urls,
        'DELETE',
        null,
        true,
        'Erro ao excluir diária'
      );
    } catch (error) {
      console.error('Erro ao excluir diária:', error);
      return { success: false, message: 'Erro ao excluir diária' };
    }
  }

  async addCusto(transacaoData) {
    try {
      const response = await this.callApi('custos', 'POST', transacaoData);
      if (response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message || 'Erro ao adicionar custo' };
    } catch (error) {
      console.error('Erro ao adicionar custo:', error);
      return { success: false, message: 'Erro ao adicionar custo' };
    }
  }
}

export const apiService = new ApiService();

// Também exporta a classe para caso seja necessário criar instâncias em contextos específicos